import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";

// ─── CONSTANTS ───────────────────────────────────────────────────
const ACC={gold:{name:"Golden",main:"#c07818",light:"#fdf4e0",mid:"#e8c47a",deep:"#8a5608"},sage:{name:"Sage",main:"#5a8a6a",light:"#edf5f0",mid:"#a8ccb4",deep:"#3a6048"},coral:{name:"Coral",main:"#e06b5a",light:"#fdf0ee",mid:"#f0b0a4",deep:"#a03828"},sky:{name:"Sky",main:"#4a7fc1",light:"#eaf1fb",mid:"#a8c4e8",deep:"#2a4e88"},lavender:{name:"Lavender",main:"#7a68c0",light:"#f0edfb",mid:"#c0b4e8",deep:"#4a38a0"},rose:{name:"Rose",main:"#c05878",light:"#fdf0f4",mid:"#e8b0c4",deep:"#803858"}};
const BGMAP={warm:"#f6f3ee",white:"#ffffff",cool:"#f0f2f5",mint:"#f0f5f2"};
const EST={essay:{h:4,l:"~4h"},hw:{h:1.5,l:"~1.5h"},proj:{h:7,l:"~7h"},exam:{h:4,l:"~4h"},read:{h:1.5,l:"~1.5h"},lab:{h:2.5,l:"~2.5h"},oth:{h:1.5,l:"~1.5h"}};
const SN={math:"Math",sci:"Science",eng:"English",his:"History",cs:"CS/Tech",oth:"Other"};
const SCOL={math:{c:"#4a7fc1",bg:"#eaf1fb",bd:"#a8c4e8"},sci:{c:"#5a8a6a",bg:"#edf5f0",bd:"#a8ccb4"},eng:{c:"#e06b5a",bg:"#fdf0ee",bd:"#f0b0a4"},his:{c:"#c8882a",bg:"#fdf3e3",bd:"#e8c48a"},cs:{c:"#7a68c0",bg:"#f0edfb",bd:"#c0b4e8"},oth:{c:"#9a9088",bg:"#f6f3ee",bd:"#d0c8bb"}};
const TYPE_COL={hw:"#4a7fc1",essay:"#7a68c0",exam:"#d04848",proj:"#c8882a",read:"#5a8a6a",lab:"#e06b5a",oth:"#9a9088"};
const PRIO_COL={Critical:{c:"#d04848",bg:"#fdf0f0",bd:"#f0b0b0"},High:{c:"#e06b5a",bg:"#fdf0ee",bd:"#f0b0a4"},Medium:{c:"#c8882a",bg:"#fdf3e3",bd:"#e8c48a"},Low:{c:"#5a8a6a",bg:"#edf5f0",bd:"#a8ccb4"}};

// ─── HELPERS ─────────────────────────────────────────────────────
const gen7=()=>Math.random().toString(36).slice(2,9).toUpperCase();
const genNum=()=>String(Math.floor(100000+Math.random()*900000));
const today=()=>{const d=new Date();d.setHours(0,0,0,0);return d;};
const dueD=s=>new Date(s+"T00:00:00");
const daysUntil=s=>Math.round((dueD(s)-today())/86400000);
const fmtDate=s=>dueD(s).toLocaleDateString("en-US",{month:"short",day:"numeric"});
function scoreA(a){const d=daysUntil(a.due);let s=d<0?100:d===0?80:d===1?65:d<=3?50:d<=7?30:10;s+=({exam:20,proj:15,essay:12,lab:10,hw:8,read:5,oth:5}[a.type]||5);const l=d<0||s>=80?"Critical":s>=60?"High":s>=35?"Medium":"Low";return{l,s};}
function dueStr(a){const d=daysUntil(a.due);if(a.done)return{t:"Done",c:"#9a9088"};if(d<0)return{t:Math.abs(d)+"d overdue",c:"#d04848"};if(d===0)return{t:"Due today",c:"#e06b5a"};if(d<=3)return{t:d+"d left",c:"#c8882a"};return{t:"Due "+fmtDate(a.due),c:"#9a9088"};}
const avatarColor=name=>{const cols=["#c07818","#5a8a6a","#e06b5a","#4a7fc1","#7a68c0","#c05878","#c8882a","#3a8a8a"];let h=0;for(const c of(name||"?"))h=(h*31+c.charCodeAt(0))%cols.length;return cols[h];};
const readFile=f=>new Promise((res,rej)=>{const r=new FileReader();r.onload=e=>res(e.target.result);r.onerror=rej;r.readAsDataURL(f);});

// ─── RESPONSIVE HOOK ─────────────────────────────────────────────
function useWindowSize(){
  const [w,setW]=useState(()=>window.innerWidth);
  useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  return{w,mob:w<640,tab:w>=640&&w<1024};
}

// ─── SHARED STORAGE (Supabase Database) ──────────────────────────
const toDocId=k=>k.replace(/[:/]/g,"_");
async function sg(k) {
  try {
    const { data, error } = await supabase
      .from("shared")
      .select("value")
      .eq("key", toDocId(k))
      .maybeSingle();
    if (error) throw error;
    return data ? data.value : null;
  } catch {
    return null;
  }
}
async function ss(k, v) {
  try {
    const { error } = await supabase
      .from("shared")
      .upsert({ key: toDocId(k), value: v, updated_at: new Date().toISOString() });
    if (error) throw error;
  } catch {}
}

function mapSupabaseUser(user) {
  if (!user) return null;
  const meta = user.user_metadata || {};
  return {
    id: user.id,
    name: meta.name || meta.full_name || "Student",
    username: meta.username || "",
    email: user.email || "",
    role: meta.role || null,
    accent: meta.accent || "gold",
    bg: meta.bg || "warm",
    photo: meta.avatar_url || meta.picture || null,
    createdAt: new Date(user.created_at).getTime()
  };
}


// ─── AVATAR ──────────────────────────────────────────────────────
function Av({name,photo,size=28}){
  const bg=avatarColor(name||"?");
  if(photo)return <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,overflow:"hidden",border:"2px solid rgba(255,255,255,.25)"}}><img src={photo} alt={name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/></div>;
  return <div style={{width:size,height:size,borderRadius:"50%",background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.42,fontWeight:800,color:"#fff",flexShrink:0,fontFamily:"'Nunito',sans-serif"}}>{(name||"?")[0].toUpperCase()}</div>;
}

// ─── LOCAL ACCOUNTS SYSTEM (Stubbed for SaaS) ────────────────────
function getAccounts(){return [];}

// ─── SIGN IN / REGISTER ──────────────────────────────────────────
function Auth({onAuth}){
  const [mode,setMode]=useState("login");
  const [loginId,setLoginId]=useState(""); // email or username
  const [pass,setPass]=useState("");
  const [pass2,setPass2]=useState("");
  const [name,setName]=useState("");
  const [username,setUsername]=useState("");
  const [email,setEmail]=useState("");
  const [err,setErr]=useState("");
  const [showPass,setShowPass]=useState(false);
  const accounts=getAccounts();

  async function login(){
    const q=loginId.toLowerCase().trim();
    let targetEmail=q;
    setErr("");
    if(!q.includes("@")){
      const {data,error}=await supabase
        .from("users")
        .select("email")
        .eq("username",q)
        .maybeSingle();
      if(error||!data){
        setErr("No account found with that username.");
        return;
      }
      targetEmail=data.email;
    }
    const {data,error}=await supabase.auth.signInWithPassword({
      email:targetEmail,
      password:pass
    });
    if(error){
      setErr(error.message);
      return;
    }
    if(data?.user){
      onAuth(mapSupabaseUser(data.user));
    }
  }

  async function register(){
    if(!name.trim()){setErr("Enter your name.");return;}
    if(!username.trim()||username.trim().length<3){setErr("Username must be at least 3 characters.");return;}
    if(!/^[a-zA-Z0-9_]+$/.test(username.trim())){setErr("Username can only contain letters, numbers, and underscores.");return;}
    if(!email.includes("@")){setErr("Enter a valid email.");return;}
    if(pass.length<4){setErr("Passcode must be at least 4 characters.");return;}
    if(pass!==pass2){setErr("Passcodes don't match.");return;}
    
    setErr("");
    const {data:existingUser}=await supabase
      .from("users")
      .select("uid")
      .or(`username.eq.${username.toLowerCase().trim()},email.eq.${email.toLowerCase().trim()}`)
      .maybeSingle();
    if(existingUser){
      setErr("Username or Email already registered.");
      return;
    }

    const {data,error}=await supabase.auth.signUp({
      email:email.toLowerCase().trim(),
      password:pass,
      options:{
        data:{
          name:name.trim(),
          username:username.trim().toLowerCase(),
          accent:"gold",
          bg:"warm",
          role:null
        }
      }
    });

    if(error){
      setErr(error.message);
      return;
    }

    const user=data.user;
    if(user){
      const myCode=user.id.slice(-7).toUpperCase();
      await supabase.from("users").insert({
        friend_code:myCode,
        uid:user.id,
        name:name.trim(),
        username:username.trim().toLowerCase(),
        email:email.toLowerCase().trim(),
        updated_at:new Date().toISOString()
      });
      onAuth(mapSupabaseUser(user));
    }
  }

  async function signInWithGoogle(){
    setErr("");
    const {error}=await supabase.auth.signInWithOAuth({
      provider:"google",
      options:{
        redirectTo:window.location.origin
      }
    });
    if(error)setErr("Google sign-in failed. Try again.");
  }

  const inp={width:"100%",background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:10,padding:"11px 14px",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:14};
  const LBL={fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:7};
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",background:"#f6f3ee",fontFamily:"'Nunito',sans-serif",padding:20}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&family=Lora:wght@500;600&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={{background:"#fff",borderRadius:20,padding:"36px 40px",maxWidth:420,width:"100%",boxShadow:"0 8px 32px rgba(42,36,32,.13)"}}>
        <div style={{fontFamily:"'Lora',serif",fontSize:26,fontWeight:600,color:"#2c2420",textAlign:"center",marginBottom:4}}>Study<span style={{color:"#c07818"}}>Desk</span></div>
        <div style={{fontSize:13,color:"#9a9088",textAlign:"center",marginBottom:28}}>{mode==="login"?"Welcome back!":"Create your account"}</div>

        {mode==="register"?(
          <>
            <div style={LBL}>Full Name</div>
            <input style={inp} value={name} onChange={e=>setName(e.target.value)} placeholder="Your display name…" autoFocus/>
            <div style={LBL}>Username</div>
            <div style={{position:"relative",marginBottom:14}}>
              <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:14,color:"#9a9088",fontWeight:700}}>@</span>
              <input style={{...inp,marginBottom:0,paddingLeft:28}} value={username} onChange={e=>setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g,""))} placeholder="yourname123"/>
            </div>
            <div style={{fontSize:11,color:"#9a9088",marginBottom:14,marginTop:-10}}>Used to log in &amp; find you as a friend</div>
            <div style={LBL}>Email</div>
            <input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@school.edu"/>
            <div style={LBL}>Passcode</div>
            <div style={{position:"relative",marginBottom:14}}>
              <input style={{...inp,marginBottom:0,paddingRight:44}} type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="Min 4 characters"/>
              <button onClick={()=>setShowPass(p=>!p)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:15,color:"#9a9088"}}>{showPass?"🙈":"👁"}</button>
            </div>
            <div style={LBL}>Confirm Passcode</div>
            <input style={inp} type="password" value={pass2} onChange={e=>setPass2(e.target.value)} onKeyDown={e=>e.key==="Enter"&&register()} placeholder="Re-enter passcode"/>
          </>
        ):(
          <>
            <div style={LBL}>Email or Username</div>
            <input style={inp} value={loginId} onChange={e=>setLoginId(e.target.value)} placeholder="you@school.edu or @username" autoFocus onKeyDown={e=>e.key==="Enter"&&login()}/>
            <div style={LBL}>Passcode</div>
            <div style={{position:"relative",marginBottom:14}}>
              <input style={{...inp,marginBottom:0,paddingRight:44}} type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} placeholder="Your passcode"/>
              <button onClick={()=>setShowPass(p=>!p)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:15,color:"#9a9088"}}>{showPass?"🙈":"👁"}</button>
            </div>
          </>
        )}

        {err&&<div style={{fontSize:12,color:"#d04848",fontWeight:700,marginBottom:12,padding:"8px 12px",background:"#fdf0f0",borderRadius:8,border:"1.5px solid #f0b0b0"}}>⚠ {err}</div>}
        <button onClick={mode==="login"?login:register} style={{width:"100%",padding:13,borderRadius:99,background:"#c07818",color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 14px #c0781855",marginBottom:10}}>
          {mode==="login"?"Sign In →":"Create Account →"}
        </button>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <div style={{flex:1,height:1,background:"#e0d9cf"}}/>
          <span style={{fontSize:11,color:"#b0a898",fontWeight:700}}>or</span>
          <div style={{flex:1,height:1,background:"#e0d9cf"}}/>
        </div>
        <button onClick={signInWithGoogle} style={{width:"100%",padding:12,borderRadius:99,background:"#fff",color:"#2c2420",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,.06)"}}>
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
          Continue with Google
        </button>
        <div style={{textAlign:"center",fontSize:13,color:"#9a9088"}}>
          {mode==="login"?<>No account? <button onClick={()=>{setMode("register");setErr("");}} style={{background:"none",border:"none",color:"#c07818",fontWeight:800,cursor:"pointer",fontFamily:"'Nunito',sans-serif",fontSize:13}}>Sign up</button></>
          :<>Already have one? <button onClick={()=>{setMode("login");setErr("");}} style={{background:"none",border:"none",color:"#c07818",fontWeight:800,cursor:"pointer",fontFamily:"'Nunito',sans-serif",fontSize:13}}>Sign in</button></>}
        </div>
        {mode==="login"&&accounts.length>0&&<div style={{marginTop:18,borderTop:"1.5px solid #e0d9cf",paddingTop:14}}>
          <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:9}}>Quick Switch</div>
          {accounts.map(a=><div key={a.id} onClick={()=>{setLoginId(a.username||a.email);setPass("");}} style={{display:"flex",alignItems:"center",gap:9,padding:"7px 10px",borderRadius:9,cursor:"pointer",marginBottom:5,border:"1.5px solid #e0d9cf",background:"#faf8f4"}} onMouseEnter={e=>e.currentTarget.style.background="#f0ede8"} onMouseLeave={e=>e.currentTarget.style.background="#faf8f4"}>
            <Av name={a.name} photo={a.photo} size={28}/>
            <div><div style={{fontSize:13,fontWeight:700,color:"#2c2420"}}>{a.name}</div><div style={{fontSize:11,color:"#9a9088"}}>@{a.username||a.email}</div></div>
          </div>)}
        </div>}
      </div>
    </div>
  );
}

// ─── ONBOARDING ──────────────────────────────────────────────────
function Onboarding({profile,onDone}){
  const [step,setStep]=useState(0);
  const [role,setRole]=useState("student");
  const [accent,setAccent]=useState("gold");
  const [bg,setBg]=useState("warm");
  const a=ACC[accent];
  const css=`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&family=Lora:wght@500;600&display=swap');*{box-sizing:border-box;margin:0;padding:0;}
  .ob{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;background:#f6f3ee;font-family:'Nunito',sans-serif;padding:20px;}
  .ob-card{background:#fff;border-radius:20px;padding:36px 40px;max-width:460px;width:100%;box-shadow:0 8px 32px rgba(42,36,32,.13);}
  .ob-btn{width:100%;padding:12px;border-radius:99px;background:${a.main};color:#fff;border:none;font-family:'Nunito',sans-serif;font-size:14px;font-weight:800;cursor:pointer;}
  .rb{padding:14px 8px;border-radius:13px;border:2px solid #e0d9cf;background:#faf8f4;cursor:pointer;text-align:center;transition:all .15s;}
  .rb:hover,.rb.sel{border-color:${a.main};background:${a.light};}
  .bg-btn{padding:10px;border-radius:10px;border:2px solid #e0d9cf;cursor:pointer;display:flex;align-items:center;gap:9px;transition:all .15s;background:#fff;}
  .bg-btn.sel,.bg-btn:hover{border-color:${a.main};background:${a.light};}`;
  const bgOpts=[{k:"warm",l:"Warm Cream",c:"#f6f3ee"},{k:"white",l:"Clean White",c:"#ffffff"},{k:"cool",l:"Cool Linen",c:"#f0f2f5"},{k:"mint",l:"Soft Mint",c:"#f0f5f2"}];
  return(
    <div className="ob"><style>{css}</style>
      <div className="ob-card">
        <div style={{fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:"#2c2420",textAlign:"center",marginBottom:4}}>Hi, {profile.name}! 👋</div>
        <div style={{fontSize:13,color:"#9a9088",textAlign:"center",marginBottom:24}}>Let's set up your workspace</div>
        <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:24}}>{[0,1,2].map(i=><div key={i} style={{width:step===i?20:8,height:8,borderRadius:99,background:step===i?a.main:"#e0d9cf",transition:"all .2s"}}/>)}</div>
        {step===0&&<>
          <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:10}}>I am a…</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:9,marginBottom:22}}>
            {[{k:"student",i:"🎒",n:"Student"},{k:"teacher",i:"📚",n:"Teacher"},{k:"personal",i:"✏️",n:"Personal"}].map(r=>(
              <div key={r.k} className={`rb${role===r.k?" sel":""}`} onClick={()=>setRole(r.k)}>
                <div style={{fontSize:26,marginBottom:5}}>{r.i}</div>
                <div style={{fontSize:12,fontWeight:800,color:"#2c2420"}}>{r.n}</div>
              </div>
            ))}
          </div>
          <button className="ob-btn" onClick={()=>setStep(1)}>Continue →</button>
        </>}
        {step===1&&<>
          <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:10}}>Accent Color</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:22}}>{Object.entries(ACC).map(([k,v])=><div key={k} onClick={()=>setAccent(k)} style={{width:36,height:36,borderRadius:"50%",background:v.main,cursor:"pointer",border:`3px solid ${accent===k?"#2c2420":"transparent"}`,transform:accent===k?"scale(1.15)":"scale(1)",transition:"all .14s"}}/>)}</div>
          <button className="ob-btn" onClick={()=>setStep(2)}>Continue →</button>
        </>}
        {step===2&&<>
          <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:10}}>Background</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:22}}>{bgOpts.map(b=><div key={b.k} className={`bg-btn${bg===b.k?" sel":""}`} onClick={()=>setBg(b.k)}><div style={{width:24,height:24,borderRadius:6,background:b.c,border:"1.5px solid #e0d9cf",flexShrink:0}}/><span style={{fontSize:12,fontWeight:700,color:"#2c2420"}}>{b.l}</span></div>)}</div>
          <button className="ob-btn" onClick={()=>onDone({role,accent,bg})}>Let's go! 🎉</button>
        </>}
      </div>
    </div>
  );
}

// ─── SETTINGS (with logout + new account + account switcher) ─────
function Settings({profile,onUpdate,onSwitchAccount,onNewAccount,onLogout,onDeleteAccount,onClose}){
  const [name,setName]=useState(profile.name);
  const [accent,setAccent]=useState(profile.accent||"gold");
  const [bg,setBg]=useState(profile.bg||"warm");
  const [role,setRole]=useState(profile.role||"student");
  const [photo,setPhoto]=useState(profile.photo||null);
  const [confirmLogout,setConfirmLogout]=useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  const photoRef=useRef(null);
  const a=ACC[accent]||ACC.gold;
  const accounts=getAccounts();
  const others=accounts.filter(ac=>ac.id!==profile.id);
  const canAddAccount=accounts.length<3;
  const bgOpts=[{k:"warm",l:"Warm Cream",c:"#f6f3ee"},{k:"white",l:"Clean White",c:"#ffffff"},{k:"cool",l:"Cool Linen",c:"#f0f2f5"},{k:"mint",l:"Soft Mint",c:"#f0f5f2"}];
  const F={fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:8,display:"block"};
  const handlePhoto=async e=>{const f=e.target.files[0];if(!f)return;try{const d=await readFile(f);setPhoto(d);}catch{}};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.45)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:20,padding:"28px 32px",width:440,maxWidth:"92vw",boxShadow:"0 8px 32px rgba(42,36,32,.16)",fontFamily:"'Nunito',sans-serif",maxHeight:"94vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:"#2c2420"}}>Settings</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#9a9088"}}>✕</button>
        </div>
        {/* Avatar */}
        <label style={F}>Profile Picture</label>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20,padding:"13px 15px",background:"#f6f3ee",borderRadius:12,border:"1.5px solid #e0d9cf"}}>
          <div style={{position:"relative",flexShrink:0}}>
            <Av name={name||profile.name} photo={photo} size={54}/>
            <button onClick={()=>photoRef.current?.click()} style={{position:"absolute",bottom:-2,right:-2,width:20,height:20,borderRadius:"50%",background:a.main,border:"2px solid #fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff"}}>+</button>
          </div>
          <div>
            <button onClick={()=>photoRef.current?.click()} style={{fontSize:11,fontWeight:700,padding:"5px 13px",borderRadius:99,background:"#fff",border:`1.5px solid ${a.mid}`,color:a.deep,cursor:"pointer",display:"block",marginBottom:5}}>{photo?"Change photo":"Upload photo"}</button>
            {photo&&<button onClick={()=>setPhoto(null)} style={{fontSize:11,fontWeight:700,padding:"5px 13px",borderRadius:99,background:"transparent",border:"1.5px solid #e0d9cf",color:"#9a9088",cursor:"pointer"}}>Remove</button>}
          </div>
          <input ref={photoRef} type="file" accept="image/*" style={{display:"none"}} onChange={handlePhoto}/>
        </div>
        {/* Name */}
        <label style={F}>Name</label>
        <input value={name} onChange={e=>setName(e.target.value)} style={{width:"100%",background:"#f6f3ee",border:`1.5px solid ${a.mid}`,borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:16}}/>
        {/* Role */}
        <label style={F}>Role</label>
        <div style={{display:"flex",gap:7,marginBottom:16}}>{["student","teacher","personal"].map(r=><button key={r} onClick={()=>setRole(r)} style={{flex:1,padding:"7px 4px",borderRadius:9,border:`2px solid ${role===r?a.main:"#e0d9cf"}`,background:role===r?a.light:"#faf8f4",fontSize:12,fontWeight:800,color:role===r?a.deep:"#5a5048",cursor:"pointer",textTransform:"capitalize"}}>{r}</button>)}</div>
        {/* Accent */}
        <label style={F}>Accent Color</label>
        <div style={{display:"flex",gap:9,marginBottom:16,flexWrap:"wrap"}}>{Object.entries(ACC).map(([k,v])=><div key={k} onClick={()=>setAccent(k)} style={{width:30,height:30,borderRadius:"50%",background:v.main,cursor:"pointer",border:`3px solid ${accent===k?"#2c2420":"transparent"}`,transform:accent===k?"scale(1.15)":"none",transition:"all .14s"}}/>)}</div>
        {/* Background */}
        <label style={F}>Background</label>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:20}}>{bgOpts.map(b=><div key={b.k} onClick={()=>setBg(b.k)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:9,border:`2px solid ${bg===b.k?a.main:"#e0d9cf"}`,background:bg===b.k?a.light:"#faf8f4",cursor:"pointer"}}><div style={{width:22,height:22,borderRadius:5,background:b.c,border:"1.5px solid #e0d9cf",flexShrink:0}}/><span style={{fontSize:12,fontWeight:700,color:"#2c2420"}}>{b.l}</span></div>)}</div>

        {/* ── Account Management ── */}
        <div style={{borderTop:"1.5px solid #e0d9cf",paddingTop:16,marginBottom:16}}>
          <label style={F}>Accounts ({accounts.length}/3)</label>
          {/* Current account */}
          <div style={{display:"flex",alignItems:"center",gap:9,padding:"9px 12px",borderRadius:10,border:`2px solid ${a.main}`,background:a.light,marginBottom:8}}>
            <Av name={name||profile.name} photo={photo} size={32}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:"#2c2420"}}>{name||profile.name} <span style={{fontSize:10,color:a.main,fontWeight:800,marginLeft:4}}>● Active</span></div>
              <div style={{fontSize:11,color:"#9a9088"}}>{profile.username?`@${profile.username} · `:""}{profile.email}</div>
            </div>
          </div>
          {/* Other accounts */}
          {others.map(ac=><div key={ac.id} onClick={()=>onSwitchAccount(ac)} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 12px",borderRadius:10,cursor:"pointer",marginBottom:6,border:"1.5px solid #e0d9cf",background:"#faf8f4",transition:"all .13s"}} onMouseEnter={e=>e.currentTarget.style.background="#f0ede8"} onMouseLeave={e=>e.currentTarget.style.background="#faf8f4"}>
            <Av name={ac.name} photo={ac.photo} size={30}/>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:"#2c2420"}}>{ac.name}</div><div style={{fontSize:11,color:"#9a9088"}}>{ac.email}</div></div>
            <span style={{fontSize:11,fontWeight:700,color:"#5a8a6a"}}>Switch →</span>
          </div>)}
          {/* Add account */}
          {canAddAccount&&<button onClick={onNewAccount} style={{width:"100%",padding:"9px 12px",borderRadius:10,border:"2px dashed #d0c8bb",background:"transparent",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:700,color:"#9a9088",cursor:"pointer",transition:"all .13s",marginTop:2}} onMouseEnter={e=>{e.currentTarget.style.borderColor=a.main;e.currentTarget.style.color=a.main;e.currentTarget.style.background=a.light;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="#d0c8bb";e.currentTarget.style.color="#9a9088";e.currentTarget.style.background="transparent";}}>＋ Add Account ({accounts.length}/3 used)</button>}
        </div>

        <button onClick={()=>{onUpdate({name:name.trim()||profile.name,accent,bg,role,photo});onClose();}} style={{width:"100%",padding:11,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:`0 4px 14px ${a.main}55`,marginBottom:10}}>Save Changes</button>
        
        {/* Sign Out */}
        {!confirmLogout
          ?<button onClick={()=>setConfirmLogout(true)} style={{width:"100%",padding:10,borderRadius:99,background:"transparent",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#9a9088",cursor:"pointer",marginBottom:8}}>Sign Out</button>
          :<div style={{display:"flex",gap:8,marginBottom:8}}>
            <button onClick={()=>setConfirmLogout(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
            <button onClick={onLogout} style={{flex:1,padding:10,borderRadius:99,background:"#d04848",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,color:"#fff",cursor:"pointer"}}>Yes, Sign Out</button>
          </div>
        }

        {/* Delete Account */}
        <button onClick={()=>setShowDeleteConfirm(true)} style={{width:"100%",padding:9,borderRadius:99,background:"transparent",border:"1.5px dashed #f0b0b0",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:700,color:"#d04848",cursor:"pointer"}}>🗑 Delete Account</button>

        {/* Delete confirmation modal */}
        {showDeleteConfirm&&(
          <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.55)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowDeleteConfirm(false)}>
            <div style={{background:"#fff",borderRadius:18,padding:"28px 30px",width:360,maxWidth:"92vw",boxShadow:"0 8px 32px rgba(208,72,72,.22)",fontFamily:"'Nunito',sans-serif"}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:40,textAlign:"center",marginBottom:10}}>⚠️</div>
              <div style={{fontFamily:"'Lora',serif",fontSize:18,fontWeight:600,color:"#d04848",textAlign:"center",marginBottom:8}}>Delete Account?</div>
              <div style={{fontSize:13,color:"#5a5048",textAlign:"center",lineHeight:1.6,marginBottom:6}}>
                Are you sure you want to delete <strong style={{color:"#2c2420"}}>{profile.name}</strong>'s account?
              </div>
              <div style={{fontSize:12,color:"#9a9088",textAlign:"center",lineHeight:1.5,marginBottom:20}}>
                All assignments, workspaces, and data will be <strong>permanently deleted</strong>. This cannot be undone.
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <button onClick={()=>setShowDeleteConfirm(false)} style={{width:"100%",padding:11,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>No, Keep My Account</button>
                <button onClick={()=>onDeleteAccount(profile.id)} style={{width:"100%",padding:11,borderRadius:99,background:"#d04848",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,color:"#fff",cursor:"pointer",boxShadow:"0 4px 14px rgba(208,72,72,.4)"}}>Yes, Delete Forever</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PLANNER ─────────────────────────────────────────────────────
function Planner({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const {mob}=useWindowSize();
  const storKey="sd_a_"+profile.id;
  const [assignments,setAssignments]=useState(()=>{const s=JSON.parse(localStorage.getItem(storKey)||"[]");s.forEach(x=>{if(!x.desc)x.desc="";if(!x.detailedNote)x.detailedNote="";});return s;});
  const [filter,setFilter]=useState("all");
  const [expandedId,setExpandedId]=useState(null);
  const [aiSnippets,setAiSnippets]=useState({});
  const [aiLoading,setAiLoading]=useState({});
  const [form,setForm]=useState({title:"",subj:"math",type:"hw",due:new Date().toISOString().split("T")[0]});
  const [GO,setGO]=useState({Critical:true,High:true,Medium:true,Low:false,done:false});
  const [showImport,setShowImport]=useState(false);
  const [importUrl,setImportUrl]=useState("");
  const [importPlatform,setImportPlatform]=useState("google");
  const [importLoading,setImportLoading]=useState(false);
  const [importMsg,setImportMsg]=useState("");
  const [customH,setCustomH]=useState(0);
  const [customM,setCustomM]=useState(25);
  const [customS,setCustomS]=useState(0);
  const [timer,setTimer]=useState({s:1500,m:1500,run:false});
  const timerRef=useRef(null);
  useEffect(()=>{localStorage.setItem(storKey,JSON.stringify(assignments));},[assignments,storKey]);

  const addA=()=>{
    if(!form.title.trim()||!form.due)return;
    const newA={id:Date.now(),title:form.title.trim(),subj:form.subj,type:form.type,due:form.due,done:false,desc:"",detailedNote:""};
    setAssignments(p=>[newA,...p]);
    setForm(p=>({...p,title:""}));
    // No auto-snippet on add — steps only shown when expanded and clicked
  };

  async function generateSteps(assign){
    const id=assign.id;
    if(aiSnippets[id]||aiLoading[id])return;
    setAiLoading(p=>({...p,[id]:true}));
    const d=daysUntil(assign.due);
    const tc=d<0?"overdue":d===0?"due today":"due in "+d+" day"+(d!==1?"s":"");
    const noteCtx=assign.detailedNote?`\nAssignment details: ${assign.detailedNote}`:"";
    // Build day labels for scheduling
    const dayLabels=[];
    const now2=new Date();
    const DOW=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const MON2=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    for(let i=0;i<=Math.max(d,0);i++){const dt=new Date(now2);dt.setDate(dt.getDate()+i);dayLabels.push(i===0?"Today":i===1?"Tomorrow":`${DOW[dt.getDay()]} ${MON2[dt.getMonth()]} ${dt.getDate()}`);}
    const schedCtx=d>0?`\nAvailable days to work: ${dayLabels.join(", ")} (due date is ${dayLabels[dayLabels.length-1] || "the last day"}).`:"";
    const prompt=`Assignment: "${assign.title}" | Type: ${assign.type} | Subject: ${SN[assign.subj]||assign.subj} | ${tc}${noteCtx}${schedCtx}

Give exactly 5 specific action steps to complete this assignment. Each step must include which day to do it based on the available days.${d<=0?" Since it's overdue/due today, mark all steps as 'Today'.":""}

Format each line EXACTLY like this (no extra text, no markdown):
Step 1: [Day] — [what to do]
Step 2: [Day] — [what to do]
Step 3: [Day] — [what to do]
Step 4: [Day] — [what to do]
Step 5: [Day] — [what to do]

Use real day names from the available days. Spread steps sensibly across the time available. Max 15 words after the dash. Plain text only.`;
    try{
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:300,messages:[{role:"user",content:prompt}]})});
      const data=await r.json();
      const txt=data.content?.map(c=>c.text||"").join("")||"";
      const steps=txt.split("\n").filter(l=>l.match(/^Step \d/)).map(l=>{
        const body=l.replace(/^Step \d+:\s*/,"").trim();
        // Split on " — " or " - " to get day + action
        const parts=body.split(/\s[—–-]\s/);
        return parts.length>=2?{day:parts[0].trim(),action:parts.slice(1).join(" — ").trim()}:{day:"",action:body};
      }).slice(0,5);
      if(steps.length)setAiSnippets(p=>({...p,[id]:steps}));
    }catch{}
    setAiLoading(p=>({...p,[id]:false}));
  }

  function importAssignments(){
    if(!importUrl.trim()&&importPlatform!=="demo"){setImportMsg("Paste your assignments page URL.");return;}
    setImportLoading(true);setImportMsg("");
    setTimeout(()=>{
      const demos={
        google:[{title:"Ch. 4 Reading Response",subj:"eng",type:"essay",due:offsetDate(2)},{title:"Quadratic Equations WS",subj:"math",type:"hw",due:offsetDate(3)},{title:"Lab Report #3",subj:"sci",type:"lab",due:offsetDate(5)}],
        teams:[{title:"History Essay Draft",subj:"his",type:"essay",due:offsetDate(1)},{title:"Coding Project Milestone",subj:"cs",type:"proj",due:offsetDate(7)},{title:"Reading Quiz",subj:"eng",type:"exam",due:offsetDate(2)}],
        canvas:[{title:"Research Paper Outline",subj:"eng",type:"essay",due:offsetDate(4)},{title:"Problem Set 6",subj:"math",type:"hw",due:offsetDate(2)},{title:"Cell Biology Review",subj:"sci",type:"read",due:offsetDate(1)}],
      };
      const demo=demos[importPlatform]||demos.google;
      const newOnes=demo.map(d=>({id:Date.now()+Math.random(),title:d.title,subj:d.subj,type:d.type,due:d.due,done:false,desc:"Imported from "+importPlatform,detailedNote:""}));
      setAssignments(p=>[...newOnes,...p]);
      setImportMsg(`✓ Imported ${newOnes.length} assignments!`);
      setImportLoading(false);
      setTimeout(()=>setShowImport(false),1500);
    },1800);
  }
  function offsetDate(days){const d=new Date();d.setDate(d.getDate()+days);return d.toISOString().split("T")[0];}

  function startCustomTimer(){const total=customH*3600+customM*60+customS;if(!total)return;setTimer({s:total,m:total,run:false});setTimeout(()=>setTimer(p=>({...p,run:true})),50);}
  function togTimer(){if(timer.run){clearInterval(timerRef.current);setTimer(p=>({...p,run:false}));}else{setTimer(p=>({...p,run:true}));}}
  function rstTimer(){clearInterval(timerRef.current);setTimer(p=>({...p,s:p.m,run:false}));}
  useEffect(()=>{if(timer.run){timerRef.current=setInterval(()=>{setTimer(p=>{if(p.s>0)return{...p,s:p.s-1};clearInterval(timerRef.current);return{...p,run:false};});},1000);}return()=>clearInterval(timerRef.current);},[timer.run]);
  const pH=Math.floor(timer.s/3600),pM=String(Math.floor((timer.s%3600)/60)).padStart(2,"0"),pS=String(timer.s%60).padStart(2,"0");
  const timerDisplay=pH>0?`${pH}:${pM}:${pS}`:`${pM}:${pS}`;

  const filtered=()=>{const t=today(),w=new Date(t);w.setDate(w.getDate()+7);return assignments.filter(a=>{const d=dueD(a.due);if(filter==="all")return true;if(filter==="active")return !a.done;if(filter==="overdue")return !a.done&&d<t;if(filter==="today")return d.toDateString()===t.toDateString();if(filter==="week")return d>=t&&d<=w&&!a.done;if(filter==="done")return a.done;return true;});};
  const list=filtered();
  const buckets={Critical:[],High:[],Medium:[],Low:[],done:[]};
  list.forEach(a=>{if(a.done){buckets.done.push(a);return;}buckets[scoreA(a).l].push(a);});
  const active=assignments.filter(a=>!a.done);
  const topA=active.length?active.map(a=>({...a,...scoreA(a)})).sort((a,b)=>b.s-a.s)[0]:null;
  const tod=today();
  const wlDays=Array.from({length:7},(_,i)=>{const d=new Date(tod);d.setDate(d.getDate()+i);return d;});
  const wlC=wlDays.map(d=>assignments.filter(a=>!a.done&&dueD(a.due).toDateString()===d.toDateString()).length);
  const wlMax=Math.max(...wlC,1);
  const DN=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const GRP=[{k:"Critical",c:"#d04848"},{k:"High",c:"#e06b5a"},{k:"Medium",c:"#c8882a"},{k:"Low",c:"#5a8a6a"},{k:"done",c:"#9a9088"}];

  function AssignRow({assign}){
    const sc=SCOL[assign.subj]||SCOL.oth;
    const pr=assign.done?null:scoreA(assign);
    const pc=pr?PRIO_COL[pr.l]:null;
    const ds=dueStr(assign);
    const isE=expandedId===assign.id;
    const steps=aiSnippets[assign.id];
    const loading=aiLoading[assign.id];
    const TYPE_ICON={hw:"📝",essay:"✍️",exam:"📖",proj:"🗂️",read:"📚",lab:"🔬",oth:"📌"};

    return(
      <div style={{borderBottom:"1.5px solid #ece8e1"}}>
        {/* Clickable header row — no checkbox, just expand */}
        <div onClick={()=>setExpandedId(isE?null:assign.id)}
          style={{padding:"11px 14px",background:isE?a.light:"transparent",transition:"background .12s",cursor:"pointer"}}
          onMouseEnter={e=>{if(!isE)e.currentTarget.style.background="#faf8f4";}}
          onMouseLeave={e=>{if(!isE)e.currentTarget.style.background="transparent";}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"center"}}>
            {/* Content */}
            <div style={{opacity:assign.done?.5:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4,flexWrap:"wrap"}}>
                <span style={{fontSize:16}}>{assign.done?"✅":TYPE_ICON[assign.type]||"📌"}</span>
                <span style={{fontSize:14,fontWeight:700,color:assign.done?"#9a9088":"#2c2420",textDecoration:assign.done?"line-through":"none"}}>{assign.title}</span>
                {pr&&pc&&<span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,border:`1.5px solid ${pc.bd}`,color:pc.c,background:pc.bg}}>{pr.l}</span>}
                {assign.done&&<span style={{fontSize:10,fontWeight:800,padding:"2px 8px",borderRadius:99,background:"#edf5f0",border:"1.5px solid #a8ccb4",color:"#3a6048"}}>✓ Done</span>}
              </div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:800,padding:"2px 8px",borderRadius:99,border:`1.5px solid ${sc.bd}`,color:sc.c,background:sc.bg}}>{SN[assign.subj]}</span>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:99,border:"1.5px solid #e0d9cf",color:"#9a9088",textTransform:"capitalize"}}>{assign.type}</span>
                <span style={{fontSize:11,fontWeight:600,color:ds.c}}>📅 {ds.t}</span>
                <span style={{fontSize:11,color:"#cfc8bb"}}>⏱ {(EST[assign.type]||EST.oth).l}</span>
                {steps?.length&&!assign.done&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:99,background:a.light,border:`1.5px solid ${a.mid}`,color:a.deep}}>✦ {steps.length} steps</span>}
              </div>
            </div>
            {/* Chevron + delete */}
            <div style={{display:"flex",gap:4,flexShrink:0,alignItems:"center"}}>
              <div style={{fontSize:10,color:"#9a9088",transform:isE?"rotate(180deg)":"none",transition:"transform .2s",padding:"4px"}}>▼</div>
              <button onClick={e=>{e.stopPropagation();setAssignments(p=>p.filter(x=>x.id!==assign.id));}}
                style={{width:26,height:26,borderRadius:6,border:"1.5px solid #e0d9cf",background:"transparent",cursor:"pointer",fontSize:12,color:"#9a9088"}}
                onMouseEnter={e=>{e.currentTarget.style.color="#d04848";e.currentTarget.style.background="#fdf0f0";}}
                onMouseLeave={e=>{e.currentTarget.style.color="#9a9088";e.currentTarget.style.background="transparent";}}>✕</button>
            </div>
          </div>
        </div>
        {/* Expanded panel */}
        {isE&&(
          <div style={{background:"#faf8f4",borderTop:"1.5px solid #e0d9cf",padding:"13px 14px 15px"}}>
            {/* Mark done / undo button — prominent at top */}
            <div style={{marginBottom:13}}>
              <button
                onClick={()=>{
                  setAssignments(p=>p.map(x=>x.id===assign.id?{...x,done:!x.done}:x));
                  // Close expand after marking done so it jumps to completed
                  if(!assign.done) setExpandedId(null);
                }}
                style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",borderRadius:99,
                  background:assign.done?"#f6f3ee":"#5a8a6a",
                  border:assign.done?"1.5px solid #d0c8bb":"none",
                  color:assign.done?"#5a5048":"#fff",
                  fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>
                {assign.done?"↩ Mark as Incomplete":"✓ Mark as Completed"}
              </button>
            </div>
            {/* Detailed note field */}
            <div style={{marginBottom:13}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:6}}>📋 Assignment Details / Requirements</div>
              <textarea
                defaultValue={assign.detailedNote||""}
                onBlur={e=>setAssignments(p=>p.map(x=>x.id===assign.id?{...x,detailedNote:e.target.value}:x))}
                placeholder="Paste the assignment description, rubric, instructions from Teams/Classroom/Canvas here… The more detail, the better your AI steps will be."
                style={{width:"100%",minHeight:70,background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:12,lineHeight:1.6,color:"#2c2420",resize:"vertical",outline:"none"}}
              />
            </div>
            {/* Personal notes */}
            <div style={{marginBottom:13}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:6}}>📝 My Notes</div>
              <textarea
                defaultValue={assign.desc||""}
                onBlur={e=>setAssignments(p=>p.map(x=>x.id===assign.id?{...x,desc:e.target.value}:x))}
                placeholder="Your personal notes, reminders, ideas…"
                style={{width:"100%",minHeight:45,background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:12,lineHeight:1.5,color:"#2c2420",resize:"vertical",outline:"none"}}
              />
            </div>
            {/* AI Steps — only shown when expanded */}
            {!assign.done&&(
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:7}}>
                  <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:a.main}}>✦ Step-by-Step Guide</div>
                  {steps&&<button onClick={()=>setAiSnippets(p=>{const n={...p};delete n[assign.id];return n;})} style={{fontSize:10,color:"#9a9088",background:"none",border:"none",cursor:"pointer",fontFamily:"'Nunito',sans-serif"}}>Regenerate</button>}
                </div>
                {steps?.length?(
                  <div style={{background:"#fff",border:`1.5px solid ${a.mid}`,borderRadius:10,padding:"10px 13px"}}>
                    {steps.map((s,i)=>{
                      const hasDay=typeof s==="object"&&s.day;
                      const action=hasDay?s.action:s;
                      const day=hasDay?s.day:"";
                      return(
                        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:i<steps.length-1?10:0}}>
                          <div style={{width:22,height:22,borderRadius:"50%",background:i===0?a.main:a.light,color:i===0?"#fff":a.deep,fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2,border:`1.5px solid ${a.mid}`}}>{i+1}</div>
                          <div style={{flex:1}}>
                            {day&&<div style={{fontSize:10,fontWeight:800,color:a.main,textTransform:"uppercase",letterSpacing:".5px",marginBottom:2}}>📅 {day}</div>}
                            <span style={{fontSize:12,color:"#2c2420",lineHeight:1.5,fontWeight:600}}>{action}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ):loading?(
                  <div style={{fontSize:11,color:a.main,fontStyle:"italic",padding:"8px 0",display:"flex",alignItems:"center",gap:6}}><span style={{display:"inline-block",animation:"spin 1s linear infinite"}}>⟳</span> Generating your step-by-step guide…</div>
                ):(
                  <button onClick={()=>generateSteps(assign)} style={{width:"100%",padding:"10px 14px",borderRadius:9,background:a.light,border:`1.5px dashed ${a.mid}`,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,color:a.deep,cursor:"pointer",textAlign:"left"}}>
                    ✦ Generate Step-by-Step Guide {assign.detailedNote?"(using your assignment details)":""}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return(
    <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>
      <div style={{flex:1,overflowY:"auto",padding:13,display:"flex",flexDirection:"column",borderRight:"1.5px solid #e0d9cf"}}>
        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr":"1fr 64px 64px 64px",gap:7,marginBottom:10}}>
          <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,padding:"11px 13px",boxShadow:"0 1px 3px rgba(42,36,32,.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
              <div><div style={{fontFamily:"'Lora',serif",fontSize:26,fontWeight:600,color:a.main,lineHeight:1}}>{assignments.length?Math.round(assignments.filter(x=>x.done).length/assignments.length*100):0}%</div><div style={{fontSize:11,color:"#9a9088",marginTop:1}}>{assignments.filter(x=>x.done).length} of {assignments.length} done</div></div>
            </div>
            <div style={{height:6,background:"#ece8e1",borderRadius:99,overflow:"hidden",border:"1.5px solid #e0d9cf"}}><div style={{height:"100%",background:`linear-gradient(90deg,${a.mid},${a.main})`,borderRadius:99,width:assignments.length?Math.round(assignments.filter(x=>x.done).length/assignments.length*100)+"%":"0%",transition:"width .6s"}}/></div>
          </div>
          {[{n:active.filter(x=>daysUntil(x.due)<=2).length,l:"Urgent",c:"#e06b5a"},{n:assignments.filter(x=>x.done).length,l:"Done",c:"#5a8a6a"},{n:(()=>{const h=active.reduce((s,x)=>s+(EST[x.type]?.h||1.5),0);return h>0?(h%1===0?h:h.toFixed(1))+"h":"0h";})(),l:"Est.",c:a.main}].map(st=>(
            <div key={st.l} style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,padding:"7px 5px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,boxShadow:"0 1px 3px rgba(42,36,32,.06)"}}>
              <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:st.c,lineHeight:1}}>{st.n}</div>
              <div style={{fontSize:10,fontWeight:800,color:"#9a9088",textTransform:"uppercase",letterSpacing:".5px"}}>{st.l}</div>
            </div>
          ))}
        </div>
        {/* Add + Import row */}
        <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,marginBottom:9,overflow:"hidden",boxShadow:"0 1px 3px rgba(42,36,32,.06)"}}>
          <div style={{display:"flex",alignItems:"center",gap:9,padding:"9px 13px",borderBottom:"1.5px solid #e0d9cf"}}>
            <span style={{fontSize:20,color:a.main}}>＋</span>
            <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addA()} placeholder="What's the assignment?" style={{flex:1,background:"transparent",border:"none",outline:"none",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:600,color:"#2c2420",caretColor:a.main}}/>
            <button onClick={()=>setShowImport(p=>!p)} style={{fontSize:11,fontWeight:800,padding:"5px 11px",borderRadius:99,border:`1.5px solid ${showImport?a.main:"#d0c8bb"}`,background:showImport?a.light:"transparent",color:showImport?a.deep:"#9a9088",cursor:"pointer",whiteSpace:"nowrap"}}>🔗 Import</button>
            <button onClick={addA} style={{background:a.main,color:"#fff",border:"none",borderRadius:99,padding:"5px 15px",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>Add</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr 1fr":"1fr 1fr 1fr"}}>
            {[{l:"Subject",id:"subj",o:[["math","Math"],["sci","Science"],["eng","English"],["his","History"],["cs","CS"],["oth","Other"]]},{l:"Type",id:"type",o:[["hw","Homework"],["essay","Essay"],["proj","Project"],["exam","Exam"],["read","Reading"],["lab","Lab"],["oth","Other"]]}].map(f=>(
              <div key={f.id} style={{padding:"7px 13px",borderRight:"1.5px solid #e0d9cf",display:"flex",flexDirection:"column",gap:2}}>
                <label style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088"}}>{f.l}</label>
                <select value={form[f.id]} onChange={e=>setForm(p=>({...p,[f.id]:e.target.value}))} style={{background:"transparent",border:"none",outline:"none",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",cursor:"pointer"}}>{f.o.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>
              </div>
            ))}
            <div style={{padding:"7px 13px",display:"flex",flexDirection:"column",gap:2}}>
              <label style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088"}}>Due Date</label>
              <input type="date" value={form.due} onChange={e=>setForm(p=>({...p,due:e.target.value}))} style={{background:"transparent",border:"none",outline:"none",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",cursor:"pointer",colorScheme:"light"}}/>
            </div>
          </div>
        </div>
        {showImport&&(
          <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,padding:"14px 16px",marginBottom:9}}>
            <div style={{fontSize:12,fontWeight:800,color:"#2c2420",marginBottom:11}}>🔗 Import Assignments</div>
            <div style={{display:"flex",gap:6,marginBottom:11,flexWrap:"wrap"}}>
              {[["google","🎓 Google Classroom"],["teams","🟦 Microsoft Teams"],["canvas","🟠 Canvas"]].map(([k,l])=>(
                <button key={k} onClick={()=>setImportPlatform(k)} style={{fontSize:11,fontWeight:700,padding:"5px 12px",borderRadius:99,border:`1.5px solid ${importPlatform===k?a.main:"#d0c8bb"}`,background:importPlatform===k?a.light:"#fff",color:importPlatform===k?a.deep:"#5a5048",cursor:"pointer"}}>{l}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <input value={importUrl} onChange={e=>setImportUrl(e.target.value)} placeholder="Paste assignments page URL (optional)…" style={{flex:1,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:9,padding:"8px 12px",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",outline:"none"}}/>
              <button onClick={importAssignments} disabled={importLoading} style={{padding:"8px 16px",borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer",opacity:importLoading?.6:1}}>{importLoading?"Importing…":"Import ✦"}</button>
            </div>
            {importMsg&&<div style={{marginTop:8,fontSize:12,fontWeight:700,color:"#5a8a6a"}}>{importMsg}</div>}
            <div style={{marginTop:8,fontSize:11,color:"#9a9088"}}>ℹ️ Demo mode auto-fills sample assignments.</div>
          </div>
        )}
        {/* Hint */}
        <div style={{fontSize:11,color:"#b0a898",fontStyle:"italic",marginBottom:8,paddingLeft:4}}>💡 Click any assignment to expand — add details, generate AI steps, and mark as done</div>
        {/* Filter chips */}
        <div style={{display:"flex",gap:4,marginBottom:9,flexWrap:"wrap"}}>
          {[["all","All"],["active","Active"],["overdue","Overdue"],["today","Today"],["week","This Week"],["done","Done"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)} style={{fontSize:12,fontWeight:700,padding:"4px 11px",borderRadius:99,border:`1.5px solid ${filter===v?a.main:"#d0c8bb"}`,background:filter===v?a.main:"#fff",color:filter===v?"#fff":"#9a9088",cursor:"pointer"}}>{l}</button>
          ))}
        </div>
        {/* Accordion */}
        {list.length===0?<div style={{textAlign:"center",padding:26,color:"#9a9088",fontStyle:"italic"}}>Nothing here yet ✨</div>:GRP.map(g=>{
          let items=buckets[g.k];if(!items?.length)return null;
          if(g.k!=="done")items=[...items].sort((a,c)=>scoreA(c).s-scoreA(a).s);
          return(
            <div key={g.k} style={{marginBottom:5}}>
              <div onClick={()=>setGO(p=>({...p,[g.k]:!p[g.k]}))} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 13px",background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:GO[g.k]?"13px 13px 0 0":"13px",cursor:"pointer",userSelect:"none"}} onMouseEnter={e=>e.currentTarget.style.background="#f6f3ee"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                <div style={{display:"flex",alignItems:"center",gap:7}}><div style={{width:8,height:8,borderRadius:"50%",background:g.c}}/><div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:g.c}}>{g.k==="done"?"Completed":g.k}</div><div style={{fontSize:11,fontWeight:700,color:"#9a9088",background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:99,padding:"1px 7px"}}>{items.length}</div></div>
                <div style={{fontSize:10,color:"#9a9088",transform:GO[g.k]?"rotate(180deg)":"none",transition:"transform .2s"}}>▼</div>
              </div>
              {GO[g.k]&&<div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderTop:"none",borderRadius:"0 0 13px 13px",overflow:"hidden"}}>{items.map(assign=><AssignRow key={assign.id} assign={assign}/>)}</div>}
            </div>
          );
        })}
      </div>
      {/* Right sidebar — hidden on mobile */}
      {!mob&&<div style={{width:255,flexShrink:0,overflowY:"auto",padding:12,display:"flex",flexDirection:"column",gap:10,background:"#faf8f4"}}>
        <div style={{background:`linear-gradient(135deg,${a.main},${a.deep})`,borderRadius:13,padding:"13px 15px",boxShadow:`0 4px 18px ${a.main}55`,color:"#fff"}}>
          <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:"1.5px",opacity:.75,marginBottom:7}}>⚡ Top Priority</div>
          {topA?<><div style={{fontFamily:"'Lora',serif",fontSize:14,fontWeight:600,lineHeight:1.35,marginBottom:5}}>{topA.title}</div><div style={{fontSize:11,opacity:.75}}>{SN[topA.subj]} · {topA.type}</div><div style={{fontSize:11,fontWeight:700,marginTop:3}}>{(()=>{const d=daysUntil(topA.due);return d<0?Math.abs(d)+"d overdue":d===0?"Due today!":"Due in "+d+"d";})()}</div></>:<div style={{fontSize:12,opacity:.6,fontStyle:"italic",textAlign:"center",padding:"6px 0"}}>All clear! 🎉</div>}
        </div>
        {/* Focus Timer */}
        <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,overflow:"hidden"}}>
          <div style={{padding:"8px 13px",borderBottom:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",gap:5}}>
            <span style={{fontSize:16}}>⏱</span><span style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088"}}>Focus Timer</span>
          </div>
          <div style={{padding:"11px 13px"}}>
            <div style={{fontFamily:"'Lora',serif",fontSize:38,fontWeight:600,color:"#2c2420",textAlign:"center",letterSpacing:-1,lineHeight:1,margin:"3px 0 2px"}}>{timerDisplay}</div>
            <div style={{height:4,background:"#ece8e1",borderRadius:99,overflow:"hidden",marginBottom:9,border:"1.5px solid #e0d9cf"}}>
              <div style={{height:"100%",background:`linear-gradient(90deg,${a.mid},${a.main})`,borderRadius:99,width:timer.m?(timer.s/timer.m*100)+"%":"0%",transition:"width 1s linear"}}/>
            </div>
            {!timer.run&&timer.s===timer.m&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,marginBottom:8}}>
                {[["h","hrs",customH,setCustomH,12],["m","min",customM,setCustomM,59],["s","sec",customS,setCustomS,59]].map(([k,l,val,setter,max])=>(
                  <div key={k} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                    <input type="number" min={0} max={max} value={val} onChange={e=>setter(Math.max(0,Math.min(max,+e.target.value||0)))} style={{width:"100%",textAlign:"center",background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:7,padding:"5px 4px",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:700,color:"#2c2420",outline:"none"}}/>
                    <span style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".5px",color:"#9a9088"}}>{l}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:5}}>
              <button onClick={()=>{if(!timer.run&&timer.s===timer.m){startCustomTimer();}else togTimer();}} style={{flex:1,background:timer.run?"#fdf0ee":a.main,color:timer.run?a.main:"#fff",border:`1.5px solid ${timer.run?a.mid:a.main}`,borderRadius:99,padding:"6px 0",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>{timer.run?"Pause":timer.s<timer.m?"Resume":"Start"}</button>
              <button onClick={rstTimer} style={{flex:1,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:99,padding:"6px 0",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,color:"#5a5048",cursor:"pointer"}}>Reset</button>
            </div>
          </div>
        </div>
        {/* 7-day workload */}
        <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,overflow:"hidden"}}>
          <div style={{padding:"8px 13px",borderBottom:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",gap:5}}><span style={{fontSize:16}}>📊</span><span style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088"}}>7-Day Workload</span></div>
          <div style={{padding:"10px 13px 13px"}}>
            <div style={{display:"flex",gap:4,alignItems:"flex-end",height:52}}>
              {wlDays.map((d,i)=>{const isT=d.toDateString()===tod.toDateString(),h=Math.round(wlC[i]/wlMax*100);return <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,height:"100%",justifyContent:"flex-end"}}><div style={{width:"100%",flex:1,background:"#ece8e1",border:"1.5px solid #e0d9cf",borderRadius:"3px 3px 0 0",borderBottom:"none",display:"flex",alignItems:"flex-end",overflow:"hidden"}}><div style={{width:"100%",background:isT?`linear-gradient(180deg,#fdf0ee,#f0b0a4)`:`linear-gradient(180deg,${a.light},${a.mid})`,height:h+"%",borderRadius:"2px 2px 0 0"}}/></div><div style={{fontSize:9,fontWeight:800,color:isT?"#e06b5a":"#9a9088",textTransform:"uppercase"}}>{DN[d.getDay()]}</div></div>;})}
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}

// ─── WORKSPACES (legacy — not rendered) ──────────────────────────
// eslint-disable-next-line no-unused-vars
function Workspaces({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const storKey="sd_ws_"+profile.id;
  const [spaces,setSpaces]=useState(()=>JSON.parse(localStorage.getItem(storKey)||"[]"));
  const [curId,setCurId]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [showInvite,setShowInvite]=useState(false);
  const [newName,setNewName]=useState("");
  const canvasRef=useRef(null);
  const ctxRef=useRef(null);
  const [tool,setTool]=useState("pencil");
  const [color,setColor]=useState("#2c2420");
  const [sz,setSz]=useState(4);
  const drawing=useRef(false);
  const undoStk=useRef([]);
  useEffect(()=>{localStorage.setItem(storKey,JSON.stringify(spaces));},[spaces,storKey]);
  const cur=spaces.find(s=>s.id===curId);
  const create=()=>{if(!newName.trim())return;const ws={id:Date.now(),name:newName.trim(),notes:"",cvData:null,code:gen7()};setSpaces(p=>[...p,ws]);setCurId(ws.id);setNewName("");setShowCreate(false);};
  const del=id=>{setSpaces(p=>p.filter(s=>s.id!==id));if(curId===id){const r=spaces.filter(s=>s.id!==id);setCurId(r.length?r[r.length-1].id:null);}};
  const saveSpace=()=>{const cv=canvasRef.current;setSpaces(p=>p.map(s=>s.id===curId?{...s,notes:document.getElementById("ws-nta-"+curId)?.value||s.notes,cvData:cv?cv.toDataURL():s.cvData}:s));const el=document.getElementById("ws-savest");if(el){el.textContent="Saved ✓";setTimeout(()=>{if(el)el.textContent="";},2000);}};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{if(!cur||!canvasRef.current)return;const cv=canvasRef.current,wrap=cv.parentElement;cv.width=wrap.clientWidth||700;cv.height=wrap.clientHeight||500;const c=cv.getContext("2d");c.lineCap="round";c.lineJoin="round";ctxRef.current=c;if(cur.cvData){const img=new Image();img.onload=()=>c.drawImage(img,0,0);img.src=cur.cvData;}undoStk.current=[];},[curId]);
  const TCFG={pencil:{lw:()=>sz,al:1,co:"source-over"},marker:{lw:()=>sz*2,al:.85,co:"source-over"},highlighter:{lw:()=>sz*5,al:.28,co:"source-over"},eraser:{lw:()=>sz*4,al:1,co:"destination-out"}};
  const SWCOLS=["#2c2420",a.main,"#e06b5a","#5a8a6a","#4a7fc1","#7a68c0","#c8882a","#e06090"];
  const pushU=()=>{const cv=canvasRef.current;if(!cv)return;undoStk.current.push(cv.toDataURL());if(undoStk.current.length>30)undoStk.current.shift();};
  const startD=(x,y)=>{if(!ctxRef.current)return;drawing.current=true;pushU();const cfg=TCFG[tool]||TCFG.pencil;const c=ctxRef.current;c.globalAlpha=cfg.al;c.globalCompositeOperation=cfg.co;c.strokeStyle=color;c.lineWidth=cfg.lw();c.beginPath();c.moveTo(x,y);};
  const doD=(x,y)=>{if(!drawing.current||!ctxRef.current)return;ctxRef.current.lineTo(x,y);ctxRef.current.stroke();};
  const endD=()=>{drawing.current=false;if(ctxRef.current){ctxRef.current.beginPath();ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";}};
  const undoC=()=>{if(!undoStk.current.length||!ctxRef.current||!canvasRef.current)return;const d=undoStk.current.pop(),img=new Image();img.onload=()=>{ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";ctxRef.current.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);ctxRef.current.drawImage(img,0,0);};img.src=d;};
  const clearC=()=>{if(!ctxRef.current||!canvasRef.current)return;pushU();ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";ctxRef.current.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);};
  const getXY=e=>{const r=canvasRef.current.getBoundingClientRect();return{x:e.clientX-r.left,y:e.clientY-r.top};};

  return(
    <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden",minHeight:0,fontFamily:"'Nunito',sans-serif"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 13px",background:"#fff",borderBottom:"1.5px solid #e0d9cf",flexShrink:0}}>
        <div style={{display:"flex",gap:5,flex:1,overflowX:"auto",padding:"2px 0"}}>
          {spaces.map(s=><div key={s.id} onClick={()=>setCurId(s.id)} style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:12,fontWeight:700,padding:"4px 11px",borderRadius:99,border:`1.5px solid ${curId===s.id?a.main:"#d0c8bb"}`,background:curId===s.id?a.light:"#f6f3ee",color:curId===s.id?a.deep:"#5a5048",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>📓 {s.name}<button onClick={e=>{e.stopPropagation();del(s.id);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,opacity:.4,color:"inherit",padding:"0 2px"}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".4"}>✕</button></div>)}
        </div>
        {cur&&<button onClick={()=>setShowInvite(true)} style={{fontSize:11,fontWeight:800,padding:"4px 11px",borderRadius:99,border:`1.5px solid ${a.mid}`,background:a.light,color:a.deep,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>👥 Invite</button>}
        <button onClick={()=>setShowCreate(true)} style={{fontSize:12,fontWeight:800,padding:"4px 13px",borderRadius:99,border:`1.5px dashed ${a.mid}`,background:"transparent",color:a.deep,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>＋ New</button>
      </div>
      {!cur?(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:9,color:"#9a9088"}}>
          <div style={{fontSize:50,opacity:.18}}>📓</div>
          <div style={{fontFamily:"'Lora',serif",fontSize:21,fontWeight:600,color:"#5a5048"}}>No Study Spaces Yet</div>
          <button onClick={()=>setShowCreate(true)} style={{marginTop:7,fontSize:13,fontWeight:800,padding:"10px 24px",borderRadius:99,background:a.main,color:"#fff",border:"none",cursor:"pointer",boxShadow:`0 4px 14px ${a.main}55`}}>＋ Create First Space</button>
        </div>
      ):(
        <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
          <div style={{width:300,flexShrink:0,display:"flex",flexDirection:"column",borderRight:"1.5px solid #e0d9cf",background:"#fff"}}>
            <div style={{padding:"9px 13px",borderBottom:"1.5px solid #e0d9cf"}}>
              <input value={cur.name} onChange={e=>setSpaces(p=>p.map(s=>s.id===curId?{...s,name:e.target.value}:s))} style={{fontFamily:"'Lora',serif",fontSize:15,fontWeight:600,color:"#2c2420",background:"transparent",border:"none",outline:"none",width:"100%"}}/>
            </div>
            <textarea id={"ws-nta-"+curId} key={curId} defaultValue={cur.notes||""} placeholder="Start writing notes here…" style={{flex:1,padding:"13px 15px",fontFamily:"'Nunito',sans-serif",fontSize:13,lineHeight:1.9,color:"#2c2420",background:"transparent",border:"none",outline:"none",resize:"none",backgroundImage:"repeating-linear-gradient(transparent,transparent 27px,#ece8e1 27px,#ece8e1 28px)",backgroundAttachment:"local"}}/>
            <div style={{padding:"7px 13px",borderTop:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",gap:8}}>
              <button onClick={saveSpace} style={{fontSize:11,fontWeight:800,padding:"4px 13px",borderRadius:99,background:"#5a8a6a",color:"#fff",border:"none",cursor:"pointer"}}>💾 Save</button>
              <span id="ws-savest" style={{fontSize:11,color:"#9a9088",fontStyle:"italic"}}></span>
            </div>
          </div>
          <div style={{flex:1,display:"flex",flexDirection:"column",background:"#f6f3ee",minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:5,padding:"6px 11px",background:"#fff",borderBottom:"1.5px solid #e0d9cf",flexWrap:"wrap",flexShrink:0}}>
              <div style={{display:"flex",gap:3}}>{[["pencil","✏️"],["marker","🖊️"],["highlighter","🖌️"],["eraser","🧹"]].map(([t,ic])=><button key={t} onClick={()=>setTool(t)} style={{width:30,height:30,borderRadius:6,border:`1.5px solid ${tool===t?a.mid:"transparent"}`,background:tool===t?a.light:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>{ic}</button>)}</div>
              <div style={{width:1,height:20,background:"#d0c8bb",margin:"0 2px"}}/>
              <div style={{display:"flex",gap:5}}>{SWCOLS.map((c,i)=><div key={i} onClick={()=>setColor(c)} style={{width:20,height:20,borderRadius:"50%",background:c,cursor:"pointer",border:`2px solid ${color===c?"#2c2420":"rgba(0,0,0,.1)"}`,transform:color===c?"scale(1.15)":"none",transition:"all .13s"}}/>)}</div>
              <div style={{width:1,height:20,background:"#d0c8bb",margin:"0 2px"}}/>
              <input type="range" min={1} max={30} value={sz} onChange={e=>setSz(+e.target.value)} style={{width:60,accentColor:a.main}}/><span style={{fontSize:11,fontWeight:700,color:"#9a9088",minWidth:16}}>{sz}</span>
              <div style={{marginLeft:"auto",display:"flex",gap:5}}>
                <button onClick={undoC} style={{fontSize:11,fontWeight:800,padding:"4px 10px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>↩ Undo</button>
                <button onClick={clearC} style={{fontSize:11,fontWeight:800,padding:"4px 10px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>Clear</button>
              </div>
            </div>
            <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:0}}>
              <canvas ref={canvasRef} style={{position:"absolute",inset:0,cursor:"crosshair",touchAction:"none"}}
                onMouseDown={e=>{const p=getXY(e);startD(p.x,p.y);}}
                onMouseMove={e=>{const p=getXY(e);doD(p.x,p.y);}}
                onMouseUp={endD} onMouseLeave={endD}
                onTouchStart={e=>{e.preventDefault();const t=e.touches[0],r=canvasRef.current.getBoundingClientRect();startD(t.clientX-r.left,t.clientY-r.top);}}
                onTouchMove={e=>{e.preventDefault();const t=e.touches[0],r=canvasRef.current.getBoundingClientRect();doD(t.clientX-r.left,t.clientY-r.top);}}
                onTouchEnd={endD}
              />
            </div>
          </div>
        </div>
      )}
      {showCreate&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowCreate(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:340,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:14}}>New Study Space</div>
            <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&create()} autoFocus placeholder="Space name…" style={{width:"100%",background:"#f6f3ee",border:`1.5px solid ${a.mid}`,borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:14}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCreate(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={create} style={{flex:1,padding:10,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Create</button>
            </div>
          </div>
        </div>
      )}
      {showInvite&&cur&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowInvite(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:360,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:6}}>Invite to "{cur.name}"</div>
            <div style={{fontSize:12,color:"#9a9088",marginBottom:16}}>Share this code with friends</div>
            <div style={{background:a.light,border:`2px solid ${a.mid}`,borderRadius:12,padding:"14px 16px",textAlign:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Lora',serif",fontSize:28,fontWeight:600,color:a.deep,letterSpacing:4}}>{cur.code}</div>
              <div style={{fontSize:11,color:"#9a9088",marginTop:4}}>Space Code</div>
            </div>
            <div style={{background:"#f6f3ee",borderRadius:9,padding:"9px 12px",fontSize:12,color:"#5a5048",fontFamily:"monospace",marginBottom:14,wordBreak:"break-all"}}>https://studydesk.app/space/{cur.code}</div>
            <button onClick={()=>setShowInvite(false)} style={{width:"100%",padding:10,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CLASSROOM ────────────────────────────────────────────────────
function Classroom({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const {mob}=useWindowSize();
  const storKey="sd_cls_"+profile.id;
  const isTeacher=profile.role==="teacher";
  const [classes,setClasses]=useState(()=>JSON.parse(localStorage.getItem(storKey)||"[]"));
  const [curId,setCurId]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [showJoin,setShowJoin]=useState(false);
  const [newCls,setNewCls]=useState({name:"",subject:"math",desc:""});
  const [joinCode,setJoinCode]=useState("");
  const [showGrade,setShowGrade]=useState(null);
  const [postForm,setPostForm]=useState({title:"",type:"hw",due:new Date().toISOString().split("T")[0],desc:""});
  const [submitText,setSubmitText]=useState({});
  const [sideOpen,setSideOpen]=useState(!mob);
  useEffect(()=>{localStorage.setItem(storKey,JSON.stringify(classes));},[classes,storKey]);
  const cur=classes.find(c=>c.id===curId);

  async function createClass(){
    if(!newCls.name.trim())return;
    const code=genNum();
    const cls={id:Date.now(),name:newCls.name.trim(),subject:newCls.subject,desc:newCls.desc,code,assignments:[],submissions:{},createdBy:profile.id};
    await ss("class:"+code,cls);
    setClasses(p=>[...p,cls]);setCurId(cls.id);setShowCreate(false);setNewCls({name:"",subject:"math",desc:""});
  }
  async function joinClass(){
    const code=joinCode.trim();if(!code){return;}
    const existing=await sg("class:"+code);
    if(!existing){alert("Class not found. Check the code and try again.");return;}
    if(classes.find(c=>c.code===code)){alert("Already in this class!");return;}
    setClasses(p=>[...p,existing]);setCurId(existing.id);setShowJoin(false);setJoinCode("");
  }
  function postAssignment(){
    if(!postForm.title.trim()||!cur)return;
    const assign={id:Date.now(),title:postForm.title.trim(),type:postForm.type,due:postForm.due,desc:postForm.desc,postedBy:profile.name,postedAt:Date.now()};
    const updated={...cur,assignments:[assign,...(cur.assignments||[])]};
    setClasses(p=>p.map(c=>c.id===cur.id?updated:c));
    ss("class:"+cur.code,updated);
    setPostForm({title:"",type:"hw",due:new Date().toISOString().split("T")[0],desc:""});
  }
  function submitWork(assignId){
    const text=submitText[assignId];if(!text?.trim())return;
    const sub={by:profile.name,uid:profile.id,text:text.trim(),at:Date.now()};
    const updated={...cur,submissions:{...cur.submissions,[assignId]:[...(cur.submissions?.[assignId]||[]),sub]}};
    setClasses(p=>p.map(c=>c.id===cur.id?updated:c));
    ss("class:"+cur.code,updated);
    setSubmitText(p=>({...p,[assignId]:""}));
  }

  const SBG="#263040",SAC="#6ee7a0";
  return(
    <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0,fontFamily:"'Nunito',sans-serif",position:"relative"}}>
      {mob&&<button onClick={()=>setSideOpen(p=>!p)} style={{position:"absolute",top:10,left:10,zIndex:20,background:SBG,border:"none",borderRadius:8,color:SAC,fontSize:18,padding:"6px 10px",cursor:"pointer"}}>{sideOpen?"✕":"☰"}</button>}
      {/* Sidebar */}
      {(!mob||sideOpen)&&(
      <div style={{width:mob?"100%":220,flexShrink:0,background:SBG,display:"flex",flexDirection:"column",overflow:"hidden",position:mob?"absolute":"relative",inset:mob?"0":undefined,zIndex:mob?10:undefined}}>
        <div style={{padding:"14px 14px 10px",borderBottom:"1px solid rgba(255,255,255,.08)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontFamily:"'Lora',serif",fontSize:15,fontWeight:600,color:"#fff",marginBottom:2}}>🏫 Classroom</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,.4)"}}>{isTeacher?"Teacher view":"Student view"}</div>
          </div>
          {mob&&<button onClick={()=>setSideOpen(false)} style={{background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:18,cursor:"pointer"}}>✕</button>}
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"8px 8px"}}>
          {classes.map(c=><div key={c.id} onClick={()=>{setCurId(c.id);if(mob)setSideOpen(false);}} style={{padding:"9px 10px",borderRadius:9,marginBottom:3,cursor:"pointer",background:curId===c.id?"rgba(110,231,160,.12)":"transparent",borderLeft:curId===c.id?`3px solid ${SAC}`:"3px solid transparent",transition:"all .14s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.06)"} onMouseLeave={e=>e.currentTarget.style.background=curId===c.id?"rgba(110,231,160,.12)":"transparent"}>
            <div style={{fontSize:13,fontWeight:700,color:curId===c.id?SAC:"#fff"}}>{c.name}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:1}}>Code: {c.code}</div>
          </div>)}
        </div>
        <div style={{padding:"10px 8px",borderTop:"1px solid rgba(255,255,255,.08)",display:"flex",flexDirection:"column",gap:5}}>
          {isTeacher&&<button onClick={()=>setShowCreate(true)} style={{width:"100%",padding:"7px 10px",borderRadius:8,background:"rgba(110,231,160,.15)",border:"1px solid rgba(110,231,160,.3)",color:SAC,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>＋ Create Class</button>}
          <button onClick={()=>setShowJoin(true)} style={{width:"100%",padding:"7px 10px",borderRadius:8,background:"rgba(255,255,255,.07)",border:"1px solid rgba(255,255,255,.12)",color:"rgba(255,255,255,.7)",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:700,cursor:"pointer"}}>Join Class</button>
        </div>
      </div>
      )}
      {/* Main */}
      {!cur?(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,color:"#9a9088",paddingTop:mob?48:0}}>
          <div style={{fontSize:50,opacity:.18}}>🏫</div>
          <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:"#5a5048"}}>{classes.length?"Select a class":"No classes yet"}</div>
          <div style={{display:"flex",gap:8}}>
            {isTeacher&&<button onClick={()=>setShowCreate(true)} style={{fontSize:13,fontWeight:800,padding:"9px 22px",borderRadius:99,background:a.main,color:"#fff",border:"none",cursor:"pointer"}}>＋ Create</button>}
            <button onClick={()=>setShowJoin(true)} style={{fontSize:13,fontWeight:800,padding:"9px 22px",borderRadius:99,border:`1.5px solid ${a.mid}`,background:a.light,color:a.deep,cursor:"pointer"}}>Join Class</button>
          </div>
        </div>
      ):(
        <div style={{flex:1,overflowY:"auto",padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:"#2c2420"}}>{cur.name}</div>
              <div style={{fontSize:12,color:"#9a9088",marginTop:2}}>Class Code: <strong style={{color:SAC.replace("6ee","5aa"),fontFamily:"monospace"}}>{cur.code}</strong></div>
            </div>
          </div>
          {isTeacher&&(
            <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,padding:"14px 16px"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#2c2420",marginBottom:11}}>📤 Post Assignment</div>
              <input value={postForm.title} onChange={e=>setPostForm(p=>({...p,title:e.target.value}))} placeholder="Assignment title…" style={{width:"100%",background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"8px 11px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:8}}/>
              <textarea value={postForm.desc} onChange={e=>setPostForm(p=>({...p,desc:e.target.value}))} placeholder="Assignment description / instructions…" style={{width:"100%",minHeight:60,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"8px 11px",fontFamily:"'Nunito',sans-serif",fontSize:12,color:"#2c2420",outline:"none",resize:"vertical",marginBottom:8}}/>
              <div style={{display:"flex",gap:8,marginBottom:10}}>
                <select value={postForm.type} onChange={e=>setPostForm(p=>({...p,type:e.target.value}))} style={{flex:1,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"7px 10px",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",outline:"none"}}>
                  {[["hw","Homework"],["essay","Essay"],["proj","Project"],["exam","Exam"],["read","Reading"],["lab","Lab"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
                <input type="date" value={postForm.due} onChange={e=>setPostForm(p=>({...p,due:e.target.value}))} style={{flex:1,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"7px 10px",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",outline:"none",colorScheme:"light"}}/>
              </div>
              <button onClick={postAssignment} style={{width:"100%",padding:9,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Post Assignment</button>
            </div>
          )}
          {(cur.assignments||[]).map(assign=>(
            <div key={assign.id} style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,overflow:"hidden"}}>
              <div style={{height:4,background:TYPE_COL[assign.type]||"#9a9088"}}/>
              <div style={{padding:"12px 15px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:15}}>{({hw:"📝",essay:"✍️",exam:"📖",proj:"🗂️",read:"📚",lab:"🔬"}[assign.type])||"📌"}</span>
                  <span style={{fontSize:15,fontWeight:700,color:"#2c2420"}}>{assign.title}</span>
                  <span style={{fontSize:11,color:"#9a9088",marginLeft:"auto"}}>Due {fmtDate(assign.due)}</span>
                </div>
                {assign.desc&&<div style={{fontSize:12,color:"#5a5048",lineHeight:1.5,marginBottom:10,padding:"8px 11px",background:"#f6f3ee",borderRadius:8}}>{assign.desc}</div>}
                {!isTeacher&&(
                  <div style={{marginTop:8}}>
                    <textarea value={submitText[assign.id]||""} onChange={e=>setSubmitText(p=>({...p,[assign.id]:e.target.value}))} placeholder="Write your submission here…" style={{width:"100%",minHeight:60,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:8,padding:"8px 11px",fontFamily:"'Nunito',sans-serif",fontSize:12,color:"#2c2420",outline:"none",resize:"vertical",marginBottom:8}}/>
                    <button onClick={()=>submitWork(assign.id)} style={{fontSize:12,fontWeight:800,padding:"7px 18px",borderRadius:99,background:a.main,color:"#fff",border:"none",cursor:"pointer"}}>Submit →</button>
                  </div>
                )}
                {isTeacher&&(cur.submissions?.[assign.id]||[]).length>0&&(
                  <button onClick={()=>setShowGrade(showGrade===assign.id?null:assign.id)} style={{fontSize:11,fontWeight:800,padding:"5px 13px",borderRadius:99,border:`1.5px solid ${a.mid}`,background:a.light,color:a.deep,cursor:"pointer",marginTop:6}}>
                    📋 {(cur.submissions[assign.id]||[]).length} submission{(cur.submissions[assign.id]||[]).length!==1?"s":""}
                  </button>
                )}
                {isTeacher&&showGrade===assign.id&&(
                  <div style={{marginTop:10}}>
                    {(cur.submissions?.[assign.id]||[]).map((sub,i)=>(
                      <div key={i} style={{background:"#f6f3ee",borderRadius:8,padding:"9px 12px",marginBottom:6}}>
                        <div style={{fontSize:11,fontWeight:800,color:"#2c2420",marginBottom:4}}>{sub.by}</div>
                        <div style={{fontSize:12,color:"#5a5048",lineHeight:1.5}}>{sub.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {(cur.assignments||[]).length===0&&<div style={{textAlign:"center",padding:30,color:"#9a9088",fontStyle:"italic"}}>{isTeacher?"No assignments posted yet.":"No assignments from your teacher yet."}</div>}
        </div>
      )}
      {/* Create class modal */}
      {showCreate&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowCreate(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:380,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:14}}>Create Class</div>
            <input value={newCls.name} onChange={e=>setNewCls(p=>({...p,name:e.target.value}))} autoFocus placeholder="Class name…" style={{width:"100%",background:"#f6f3ee",border:`1.5px solid ${a.mid}`,borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:10}}/>
            <textarea value={newCls.desc} onChange={e=>setNewCls(p=>({...p,desc:e.target.value}))} placeholder="Class description (optional)…" style={{width:"100%",minHeight:60,background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:12,color:"#2c2420",outline:"none",resize:"none",marginBottom:14}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCreate(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={createClass} style={{flex:1,padding:10,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Create</button>
            </div>
          </div>
        </div>
      )}
      {showJoin&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowJoin(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:340,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:6}}>Join a Class</div>
            <div style={{fontSize:12,color:"#9a9088",marginBottom:14}}>Enter the 6-digit class code from your teacher</div>
            <input value={joinCode} onChange={e=>setJoinCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&joinClass()} autoFocus placeholder="e.g. 482910" style={{width:"100%",background:"#f6f3ee",border:`1.5px solid ${a.mid}`,borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:18,fontWeight:700,color:"#2c2420",outline:"none",marginBottom:14,letterSpacing:3,textAlign:"center"}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowJoin(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={joinClass} style={{flex:1,padding:10,borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Join →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── STUDY GROUPS + SPACES (INTEGRATED) ──────────────────────────
function StudyGroups({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const {mob}=useWindowSize();
  const storKey="sd_sg_"+profile.id;
  const wsKey="sd_ws_"+profile.id;
  const [groups,setGroups]=useState(()=>JSON.parse(localStorage.getItem(storKey)||"[]"));
  const [spaces,setSpaces]=useState(()=>JSON.parse(localStorage.getItem(wsKey)||"[]"));
  const [sectionView,setSectionView]=useState("groups"); // "groups" | "spaces"
  const [curId,setCurId]=useState(null);
  const [curSpaceId,setCurSpaceId]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [showJoin,setShowJoin]=useState(false);
  const [showCreateSpace,setShowCreateSpace]=useState(false);
  const [showSpacePanel,setShowSpacePanel]=useState(false);
  const [sideOpen,setSideOpen]=useState(!mob);
  const [showInviteFriends,setShowInviteFriends]=useState(false);
  const [groupInvites,setGroupInvites]=useState([]);
  const myFriends=JSON.parse(localStorage.getItem("sd_friends_"+profile.id)||"[]").filter(f=>f.status==="accepted");
  const [activeThread,setActiveThread]=useState("general");
  const [messages,setMessages]=useState({});
  const [input,setInput]=useState("");
  const [sending,setSending]=useState(false);
  const [newGrp,setNewGrp]=useState({name:"",subject:"math"});
  const [joinCode,setJoinCode]=useState("");
  const [activeView,setActiveView]=useState("chat"); // chat | space
  const [newSpaceName,setNewSpaceName]=useState("");
  const fileRef=useRef(null);
  const inputRef=useRef(null);
  const msgEndRef=useRef(null);
  const canvasRef=useRef(null);
  const ctxRef=useRef(null);
  const [tool,setTool]=useState("pencil");
  const [color,setColor]=useState("#2c2420");
  const [sz,setSz]=useState(4);
  const drawing=useRef(false);
  const undoStk=useRef([]);
  useEffect(()=>{localStorage.setItem(storKey,JSON.stringify(groups));},[groups,storKey]);
  useEffect(()=>{localStorage.setItem(wsKey,JSON.stringify(spaces));},[spaces,wsKey]);

  const cur=groups.find(g=>g.id===curId);
  const THREADS=["general","homework","notes","questions","resources","off-topic"];
  const THREAD_ICONS={general:"💬",homework:"📝",notes:"📄",questions:"❓",resources:"📎","off-topic":"😄"};
  const GRP_ICONS={math:"📐",sci:"🔬",eng:"📖",his:"🗺️",cs:"💻",oth:"📚"};
  const SWCOLS=["#2c2420",a.main,"#e06b5a","#5a8a6a","#4a7fc1","#7a68c0"];

  const threadKey=cur?`sg:${cur.code}:${activeThread}`:null;

  useEffect(()=>{
    if(!threadKey)return;
    sg(threadKey).then(msgs => {
      if(msgs) setMessages(p => ({...p, [activeThread]: msgs}));
    });
    const channel = supabase
      .channel(`shared_thread_${toDocId(threadKey)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shared", filter: `key=eq.${toDocId(threadKey)}` },
        (payload) => {
          if (payload.new && payload.new.value) {
            setMessages(p => ({...p, [activeThread]: payload.new.value}));
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },[threadKey,activeThread]);

  useEffect(()=>{msgEndRef.current?.scrollIntoView({behavior:"smooth"});},[messages,activeThread]);

  // Listen for incoming group invites
  useEffect(()=>{
    const loadInvites = async () => {
      const { data } = await supabase
        .from("group_invites")
        .select("invites")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (data?.invites) {
        setGroupInvites(Object.entries(data.invites).map(([code, info]) => ({ code, ...info })));
      } else {
        setGroupInvites([]);
      }
    };
    loadInvites();

    const channel = supabase
      .channel(`group_invites_${profile.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "group_invites", filter: `user_id=eq.${profile.id}` },
        (payload) => {
          const invites = payload.new ? payload.new.invites : {};
          setGroupInvites(Object.entries(invites || {}).map(([code, info]) => ({ code, ...info })));
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },[profile.id]);

  async function inviteFriendToGroup(friend){
    if(!cur)return;
    const alreadyMember=(cur.members||[]).find(m=>m.uid===friend.uid);
    if(alreadyMember){alert(`${friend.name} is already in this group.`);return;}
    try {
      const { data } = await supabase
        .from("group_invites")
        .select("invites")
        .eq("user_id", friend.uid)
        .maybeSingle();
      const currentInvites = data?.invites || {};
      const newInvites = {
        ...currentInvites,
        [cur.code]: {groupName:cur.name,groupSubject:cur.subject,invitedByName:profile.name,invitedByUid:profile.id,sentAt:Date.now()}
      };
      await supabase
        .from("group_invites")
        .upsert({ user_id: friend.uid, invites: newInvites, updated_at: new Date().toISOString() });
    } catch {}
    setShowInviteFriends(false);
    alert(`Invite sent to ${friend.name}!`);
  }
  async function acceptGroupInvite(invite){
    const info=await sg("sg:"+invite.code+":info");
    if(!info){alert("Group not found.");return;}
    if(groups.find(g=>g.code===invite.code)){alert("Already in this group!");return;}
    const updated={...info,members:[...(info.members||[]),{uid:profile.id,name:profile.name}]};
    await ss("sg:"+invite.code+":info",updated);
    setGroups(p=>[...p,updated]);setCurId(updated.id);setSectionView("groups");
    
    // Remove invite from Supabase
    try {
      const { data } = await supabase
        .from("group_invites")
        .select("invites")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (data?.invites) {
        const updatedInvites = { ...data.invites };
        delete updatedInvites[invite.code];
        await supabase
          .from("group_invites")
          .upsert({ user_id: profile.id, invites: updatedInvites, updated_at: new Date().toISOString() });
      }
    } catch {}
  }
  async function declineGroupInvite(invite){
    try {
      const { data } = await supabase
        .from("group_invites")
        .select("invites")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (data?.invites) {
        const updatedInvites = { ...data.invites };
        delete updatedInvites[invite.code];
        await supabase
          .from("group_invites")
          .upsert({ user_id: profile.id, invites: updatedInvites, updated_at: new Date().toISOString() });
      }
    } catch {}
  }

  function createSpace(){
    if(!newSpaceName.trim())return;
    const ws={id:Date.now(),name:newSpaceName.trim(),notes:"",cvData:null,code:gen7()};
    setSpaces(p=>[...p,ws]);setCurSpaceId(ws.id);setNewSpaceName("");setShowCreateSpace(false);setSectionView("spaces");
  }
  function deleteSpace(id){setSpaces(p=>p.filter(s=>s.id!==id));if(curSpaceId===id)setCurSpaceId(null);}

  // Canvas helpers (used by both group Space view and personal Spaces)
  const wsCanvasRef=useRef(null);
  const wsCtxRef=useRef(null);
  const wsDrawing=useRef(false);
  const wsUndoStk=useRef([]);
  const [wsTool,setWsTool]=useState("pencil");
  const [wsColor,setWsColor]=useState("#2c2420");
  const [wsSz,setWsSz]=useState(4);
  useEffect(()=>{
    if(!curSpaceId||!wsCanvasRef.current||sectionView!=="spaces")return;
    const cv=wsCanvasRef.current,wrap=cv.parentElement;
    if(!wrap)return;
    cv.width=wrap.clientWidth||700;cv.height=wrap.clientHeight||500;
    const c=cv.getContext("2d");c.lineCap="round";c.lineJoin="round";wsCtxRef.current=c;
    const cur2=spaces.find(s=>s.id===curSpaceId);
    if(cur2?.cvData){const img=new Image();img.onload=()=>c.drawImage(img,0,0);img.src=cur2.cvData;}
    wsUndoStk.current=[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[curSpaceId,sectionView]);
  const TCFG2={pencil:{lw:()=>wsSz,al:1,co:"source-over"},marker:{lw:()=>wsSz*2,al:.85,co:"source-over"},highlighter:{lw:()=>wsSz*5,al:.28,co:"source-over"},eraser:{lw:()=>wsSz*4,al:1,co:"destination-out"}};
  const wsPushU=()=>{const cv=wsCanvasRef.current;if(!cv)return;wsUndoStk.current.push(cv.toDataURL());if(wsUndoStk.current.length>30)wsUndoStk.current.shift();};
  const wsStartD=(x,y)=>{if(!wsCtxRef.current)return;wsDrawing.current=true;wsPushU();const cfg=TCFG2[wsTool]||TCFG2.pencil;const c=wsCtxRef.current;c.globalAlpha=cfg.al;c.globalCompositeOperation=cfg.co;c.strokeStyle=wsColor;c.lineWidth=cfg.lw();c.beginPath();c.moveTo(x,y);};
  const wsDoD=(x,y)=>{if(!wsDrawing.current||!wsCtxRef.current)return;wsCtxRef.current.lineTo(x,y);wsCtxRef.current.stroke();};
  const wsEndD=()=>{wsDrawing.current=false;if(wsCtxRef.current){wsCtxRef.current.beginPath();wsCtxRef.current.globalAlpha=1;wsCtxRef.current.globalCompositeOperation="source-over";}};
  const wsUndoC=()=>{if(!wsUndoStk.current.length||!wsCtxRef.current||!wsCanvasRef.current)return;const d=wsUndoStk.current.pop(),img=new Image();img.onload=()=>{wsCtxRef.current.clearRect(0,0,wsCanvasRef.current.width,wsCanvasRef.current.height);wsCtxRef.current.drawImage(img,0,0);};img.src=d;};
  const wsClearC=()=>{if(!wsCtxRef.current||!wsCanvasRef.current)return;wsPushU();wsCtxRef.current.clearRect(0,0,wsCanvasRef.current.width,wsCanvasRef.current.height);};
  const wsGetXY=e=>{const r=wsCanvasRef.current.getBoundingClientRect();return{x:e.clientX-r.left,y:e.clientY-r.top};};
  const saveWsSpace=()=>{const cv=wsCanvasRef.current;const ntaEl=document.getElementById("ws-nta-"+curSpaceId);setSpaces(p=>p.map(s=>s.id===curSpaceId?{...s,notes:ntaEl?.value||s.notes,cvData:cv?cv.toDataURL():s.cvData}:s));};
  const SWCOLS2=["#2c2420",a.main,"#e06b5a","#5a8a6a","#4a7fc1","#7a68c0","#c8882a","#e06090"];

  async function createGroup(){
    if(!newGrp.name.trim())return;
    const code=gen7();
    const grp={id:Date.now(),name:newGrp.name.trim(),subject:newGrp.subject,code,createdBy:profile.id,members:[{uid:profile.id,name:profile.name}]};
    await ss("sg:"+code+":info",grp);
    setGroups(p=>[...p,grp]);setCurId(grp.id);setShowCreate(false);setNewGrp({name:"",subject:"math"});setSectionView("groups");
  }
  async function joinGroup(){
    const code=joinCode.trim().toUpperCase();if(!code)return;
    const info=await sg("sg:"+code+":info");
    if(!info){alert("Group not found. Check the code and try again.");return;}
    if(groups.find(g=>g.code===code)){alert("Already in this group!");return;}
    const updated={...info,members:[...(info.members||[]),{uid:profile.id,name:profile.name}]};
    await ss("sg:"+code+":info",updated);
    setGroups(p=>[...p,updated]);setCurId(updated.id);setShowJoin(false);setJoinCode("");setSectionView("groups");
  }
  async function sendMessage(text){
    if(!text?.trim()||!cur)return;
    setSending(true);
    const msg={id:Date.now(),uid:profile.id,name:profile.name,text:text.trim(),at:Date.now(),thread:activeThread};
    const existing=await sg(threadKey)||[];
    const updated=[...existing,msg].slice(-200);
    await ss(threadKey,updated);
    setMessages(p=>({...p,[activeThread]:updated}));
    setInput("");setSending(false);
    setTimeout(()=>inputRef.current?.focus(),50);
  }
  async function sendFile(file){
    if(!cur)return;
    const msg={id:Date.now(),uid:profile.id,name:profile.name,text:`📎 ${file.name}`,fileType:file.type,at:Date.now(),thread:activeThread};
    const existing=await sg(threadKey)||[];
    const updated=[...existing,msg].slice(-200);
    await ss(threadKey,updated);
    setMessages(p=>({...p,[activeThread]:updated}));
  }
  async function shareSpace(space){
    if(!cur)return;
    const msg={id:Date.now(),uid:profile.id,name:profile.name,text:`📓 Shared a study space: "${space.name}"`,spaceCode:space.code,spaceName:space.name,at:Date.now(),thread:activeThread};
    const existing=await sg(threadKey)||[];
    const updated=[...existing,msg].slice(-200);
    await ss(threadKey,updated);
    setMessages(p=>({...p,[activeThread]:updated}));
    setShowSpacePanel(false);
  }

  // Group shared canvas (for group Space view)
  useEffect(()=>{
    if(activeView!=="space"||!cur||!canvasRef.current)return;
    const cv=canvasRef.current,wrap=cv.parentElement;
    cv.width=wrap.clientWidth||700;cv.height=wrap.clientHeight||500;
    const c=cv.getContext("2d");c.lineCap="round";c.lineJoin="round";ctxRef.current=c;
    undoStk.current=[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activeView,curId]);

  const TCFG={pencil:{lw:()=>sz,al:1,co:"source-over"},marker:{lw:()=>sz*2,al:.85,co:"source-over"},highlighter:{lw:()=>sz*5,al:.28,co:"source-over"},eraser:{lw:()=>sz*4,al:1,co:"destination-out"}};
  const pushU=()=>{const cv=canvasRef.current;if(!cv)return;undoStk.current.push(cv.toDataURL());if(undoStk.current.length>30)undoStk.current.shift();};
  const startD=(x,y)=>{if(!ctxRef.current)return;drawing.current=true;pushU();const cfg=TCFG[tool]||TCFG.pencil;const c=ctxRef.current;c.globalAlpha=cfg.al;c.globalCompositeOperation=cfg.co;c.strokeStyle=color;c.lineWidth=cfg.lw();c.beginPath();c.moveTo(x,y);};
  const doD=(x,y)=>{if(!drawing.current||!ctxRef.current)return;ctxRef.current.lineTo(x,y);ctxRef.current.stroke();};
  const endD=()=>{drawing.current=false;if(ctxRef.current){ctxRef.current.beginPath();ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";}};
  const undoC=()=>{if(!undoStk.current.length||!ctxRef.current||!canvasRef.current)return;const d=undoStk.current.pop(),img=new Image();img.onload=()=>{ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";ctxRef.current.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);ctxRef.current.drawImage(img,0,0);};img.src=d;};
  const clearC=()=>{if(!ctxRef.current||!canvasRef.current)return;pushU();ctxRef.current.globalAlpha=1;ctxRef.current.globalCompositeOperation="source-over";ctxRef.current.clearRect(0,0,canvasRef.current.width,canvasRef.current.height);};
  const getXY=e=>{const r=canvasRef.current.getBoundingClientRect();return{x:e.clientX-r.left,y:e.clientY-r.top};};

  const threadMsgs=messages[activeThread]||[];
  const SBG="#1c1a2e",SAC="#a884ff";
  const curSpace=spaces.find(s=>s.id===curSpaceId);

  return(
    <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0,fontFamily:"'Nunito',sans-serif",position:"relative"}}>
      {mob&&<button onClick={()=>setSideOpen(p=>!p)} style={{position:"absolute",top:10,left:10,zIndex:20,background:SBG,border:"none",borderRadius:8,color:SAC,fontSize:18,padding:"6px 10px",cursor:"pointer"}}>{sideOpen?"✕":"☰"}</button>}
      {/* Sidebar */}
      {(!mob||sideOpen)&&(
      <div style={{width:mob?"100%":230,flexShrink:0,background:SBG,display:"flex",flexDirection:"column",overflow:"hidden",position:mob?"absolute":"relative",inset:mob?"0":undefined,zIndex:mob?10:undefined}}>
        {/* Section toggle at top */}
        <div style={{padding:"10px 10px 8px",borderBottom:"1px solid rgba(255,255,255,.08)",display:"flex",alignItems:"center",gap:8}}>
          <div style={{display:"flex",background:"rgba(255,255,255,.07)",borderRadius:99,padding:3,gap:2,flex:1}}>
            <button onClick={()=>setSectionView("groups")} style={{flex:1,padding:"5px 8px",borderRadius:99,background:sectionView==="groups"?SAC:"transparent",color:sectionView==="groups"?"#1c1a2e":"rgba(255,255,255,.6)",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer",transition:"all .14s"}}>📚 Groups</button>
            <button onClick={()=>setSectionView("spaces")} style={{flex:1,padding:"5px 8px",borderRadius:99,background:sectionView==="spaces"?SAC:"transparent",color:sectionView==="spaces"?"#1c1a2e":"rgba(255,255,255,.6)",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer",transition:"all .14s"}}>📓 Spaces</button>
          </div>
          {mob&&<button onClick={()=>setSideOpen(false)} style={{background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:18,cursor:"pointer",flexShrink:0}}>✕</button>}
        </div>

        {sectionView==="groups"?(
          <>
            <div style={{flex:1,overflowY:"auto",padding:"8px 8px"}}>
              {groups.length===0&&<div style={{padding:"16px 10px",fontSize:11,color:"rgba(255,255,255,.3)",textAlign:"center",fontStyle:"italic"}}>No groups yet</div>}
              {groups.map(g=><div key={g.id} onClick={()=>{setCurId(g.id);setActiveView("chat");if(mob)setSideOpen(false);}} style={{padding:"9px 10px",borderRadius:9,marginBottom:3,cursor:"pointer",background:curId===g.id?"rgba(168,132,255,.15)":"transparent",borderLeft:curId===g.id?`3px solid ${SAC}`:"3px solid transparent"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.06)"} onMouseLeave={e=>e.currentTarget.style.background=curId===g.id?"rgba(168,132,255,.15)":"transparent"}>
                <div style={{fontSize:13,fontWeight:700,color:curId===g.id?SAC:"#fff"}}>{GRP_ICONS[g.subject]||"📚"} {g.name}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:1}}>Code: {g.code}</div>
              </div>)}
            </div>
            {groupInvites.length>0&&(
              <div style={{padding:"8px 8px",borderTop:"1px solid rgba(255,255,255,.08)"}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"rgba(168,132,255,.7)",marginBottom:6,paddingLeft:2}}>📬 Invites ({groupInvites.length})</div>
                {groupInvites.map(inv=>(
                  <div key={inv.code} style={{background:"rgba(168,132,255,.1)",border:"1px solid rgba(168,132,255,.25)",borderRadius:9,padding:"8px 10px",marginBottom:5}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#fff",marginBottom:2}}>{inv.groupName}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,.5)",marginBottom:6}}>from {inv.invitedByName}</div>
                    <div style={{display:"flex",gap:5}}>
                      <button onClick={()=>acceptGroupInvite(inv)} style={{flex:1,padding:"5px 0",borderRadius:99,background:SAC,color:"#1c1a2e",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer"}}>Join</button>
                      <button onClick={()=>declineGroupInvite(inv)} style={{padding:"5px 9px",borderRadius:99,background:"transparent",border:"1px solid rgba(255,255,255,.2)",color:"rgba(255,255,255,.5)",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:700,cursor:"pointer"}}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{padding:"10px 8px",borderTop:"1px solid rgba(255,255,255,.08)",display:"flex",flexDirection:"column",gap:5}}>
              <button onClick={()=>setShowCreate(true)} style={{width:"100%",padding:"7px 10px",borderRadius:8,background:"rgba(168,132,255,.15)",border:"1px solid rgba(168,132,255,.3)",color:SAC,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>＋ Create Group</button>
              <button onClick={()=>setShowJoin(true)} style={{width:"100%",padding:"7px 10px",borderRadius:8,background:"rgba(255,255,255,.07)",border:"1px solid rgba(255,255,255,.12)",color:"rgba(255,255,255,.7)",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:700,cursor:"pointer"}}>Join Group</button>
            </div>
          </>
        ):(
          <>
            <div style={{flex:1,overflowY:"auto",padding:"8px 8px"}}>
              {spaces.length===0&&<div style={{padding:"16px 10px",fontSize:11,color:"rgba(255,255,255,.3)",textAlign:"center",fontStyle:"italic"}}>No spaces yet</div>}
              {spaces.map(s=><div key={s.id} onClick={()=>{setCurSpaceId(s.id);if(mob)setSideOpen(false);}} style={{padding:"9px 10px",borderRadius:9,marginBottom:3,cursor:"pointer",background:curSpaceId===s.id?"rgba(168,132,255,.15)":"transparent",borderLeft:curSpaceId===s.id?`3px solid ${SAC}`:"3px solid transparent",display:"flex",alignItems:"center",justifyContent:"space-between"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.06)"} onMouseLeave={e=>e.currentTarget.style.background=curSpaceId===s.id?"rgba(168,132,255,.15)":"transparent"}>
                <div style={{fontSize:13,fontWeight:700,color:curSpaceId===s.id?SAC:"#fff",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📓 {s.name}</div>
                <button onClick={e=>{e.stopPropagation();deleteSpace(s.id);}} style={{background:"none",border:"none",fontSize:11,color:"rgba(255,255,255,.3)",cursor:"pointer",flexShrink:0,padding:"2px 4px"}} onMouseEnter={e=>e.currentTarget.style.color="#ff8080"} onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,.3)"}>✕</button>
              </div>)}
            </div>
            <div style={{padding:"10px 8px",borderTop:"1px solid rgba(255,255,255,.08)"}}>
              <button onClick={()=>setShowCreateSpace(true)} style={{width:"100%",padding:"7px 10px",borderRadius:8,background:"rgba(168,132,255,.15)",border:"1px solid rgba(168,132,255,.3)",color:SAC,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>＋ New Space</button>
            </div>
          </>
        )}
      </div>
      )}

      {/* ── Main content area switches between Spaces and Groups ── */}
      {sectionView==="spaces"?(
        !curSpace?(
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,color:"#9a9088"}}>
            <div style={{fontSize:50,opacity:.18}}>📓</div>
            <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:"#5a5048"}}>No Study Spaces Yet</div>
            <div style={{fontSize:13,color:"#9a9088",marginBottom:4}}>Create a personal notes + whiteboard space</div>
            <button onClick={()=>setShowCreateSpace(true)} style={{fontSize:13,fontWeight:800,padding:"10px 24px",borderRadius:99,background:SAC,color:"#1c1a2e",border:"none",cursor:"pointer"}}>＋ Create First Space</button>
          </div>
        ):(
          <div style={{flex:1,display:"flex",flexDirection:mob?"column":"row",overflow:"hidden",minHeight:0}}>
            {mob&&(
              <div style={{flexShrink:0,background:"#fff",borderBottom:"1.5px solid #e0d9cf",display:"flex",padding:"6px 10px",gap:6}}>
                <button onClick={()=>setActiveView("chat")} style={{flex:1,padding:"6px 0",borderRadius:99,background:activeView!=="space"?"#5a8a6a":"transparent",color:activeView!=="space"?"#fff":"#9a9088",border:`1.5px solid ${activeView!=="space"?"#5a8a6a":"#d0c8bb"}`,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>📄 Notes</button>
                <button onClick={()=>setActiveView("space")} style={{flex:1,padding:"6px 0",borderRadius:99,background:activeView==="space"?"#7a68c0":"transparent",color:activeView==="space"?"#fff":"#9a9088",border:`1.5px solid ${activeView==="space"?"#7a68c0":"#d0c8bb"}`,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>🖌 Canvas</button>
              </div>
            )}
            {(!mob||activeView!=="space")&&<div style={{width:mob?"100%":300,flexShrink:0,display:"flex",flexDirection:"column",borderRight:mob?"none":"1.5px solid #e0d9cf",background:"#fff"}}>
              <div style={{padding:"9px 13px",borderBottom:"1.5px solid #e0d9cf"}}>
                <input value={curSpace.name} onChange={e=>setSpaces(p=>p.map(s=>s.id===curSpaceId?{...s,name:e.target.value}:s))} style={{fontFamily:"'Lora',serif",fontSize:15,fontWeight:600,color:"#2c2420",background:"transparent",border:"none",outline:"none",width:"100%"}}/>
              </div>
              <textarea id={"ws-nta-"+curSpaceId} key={curSpaceId} defaultValue={curSpace.notes||""} placeholder="Start writing notes here…" style={{flex:1,padding:"13px 15px",fontFamily:"'Nunito',sans-serif",fontSize:13,lineHeight:1.9,color:"#2c2420",background:"transparent",border:"none",outline:"none",resize:"none",backgroundImage:"repeating-linear-gradient(transparent,transparent 27px,#ece8e1 27px,#ece8e1 28px)",backgroundAttachment:"local"}}/>
              <div style={{padding:"7px 13px",borderTop:"1.5px solid #e0d9cf"}}>
                <button onClick={saveWsSpace} style={{fontSize:11,fontWeight:800,padding:"4px 13px",borderRadius:99,background:"#5a8a6a",color:"#fff",border:"none",cursor:"pointer"}}>💾 Save</button>
              </div>
            </div>}
            {(!mob||activeView==="space")&&<div style={{flex:1,display:"flex",flexDirection:"column",background:"#f6f3ee",minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:5,padding:"6px 11px",background:"#fff",borderBottom:"1.5px solid #e0d9cf",flexWrap:"wrap",flexShrink:0}}>
                <div style={{display:"flex",gap:3}}>{[["pencil","✏️"],["marker","🖊️"],["highlighter","🖌️"],["eraser","🧹"]].map(([t,ic])=><button key={t} onClick={()=>setWsTool(t)} style={{width:30,height:30,borderRadius:6,border:`1.5px solid ${wsTool===t?a.mid:"transparent"}`,background:wsTool===t?a.light:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>{ic}</button>)}</div>
                <div style={{width:1,height:20,background:"#d0c8bb"}}/>
                <div style={{display:"flex",gap:4}}>{SWCOLS2.map((c2,i)=><div key={i} onClick={()=>setWsColor(c2)} style={{width:19,height:19,borderRadius:"50%",background:c2,cursor:"pointer",border:`2px solid ${wsColor===c2?"#2c2420":"rgba(0,0,0,.08)"}`,transform:wsColor===c2?"scale(1.15)":"none",transition:"all .12s"}}/>)}</div>
                <div style={{width:1,height:20,background:"#d0c8bb"}}/>
                <input type="range" min={1} max={30} value={wsSz} onChange={e=>setWsSz(+e.target.value)} style={{width:56,accentColor:a.main}}/><span style={{fontSize:10,color:"#9a9088",minWidth:14}}>{wsSz}</span>
                <div style={{marginLeft:"auto",display:"flex",gap:5}}>
                  <button onClick={wsUndoC} style={{fontSize:11,fontWeight:800,padding:"4px 9px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>↩</button>
                  <button onClick={wsClearC} style={{fontSize:11,fontWeight:800,padding:"4px 9px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>Clear</button>
                </div>
              </div>
              <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:0}}>
                <canvas ref={wsCanvasRef} style={{position:"absolute",inset:0,cursor:"crosshair",touchAction:"none"}}
                  onMouseDown={e=>{const p=wsGetXY(e);wsStartD(p.x,p.y);}}
                  onMouseMove={e=>{const p=wsGetXY(e);wsDoD(p.x,p.y);}}
                  onMouseUp={wsEndD} onMouseLeave={wsEndD}
                  onTouchStart={e=>{e.preventDefault();const t=e.touches[0],r=wsCanvasRef.current.getBoundingClientRect();wsStartD(t.clientX-r.left,t.clientY-r.top);}}
                  onTouchMove={e=>{e.preventDefault();const t=e.touches[0],r=wsCanvasRef.current.getBoundingClientRect();wsDoD(t.clientX-r.left,t.clientY-r.top);}}
                  onTouchEnd={wsEndD}
                />
              </div>
            </div>}
          </div>
        )
      ):(
        <>
      {!cur?(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,color:"#9a9088"}}>
          <div style={{fontSize:50,opacity:.18}}>📚</div>
          <div style={{fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:"#5a5048"}}>No Study Groups Yet</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowCreate(true)} style={{fontSize:13,fontWeight:800,padding:"9px 22px",borderRadius:99,background:SAC,color:"#1c1a2e",border:"none",cursor:"pointer"}}>＋ Create</button>
            <button onClick={()=>setShowJoin(true)} style={{fontSize:13,fontWeight:800,padding:"9px 22px",borderRadius:99,border:"1.5px solid #c0b4e8",background:"#f0edfb",color:"#4a38a0",cursor:"pointer"}}>Join Group</button>
          </div>
        </div>
      ):(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}>
          {/* Group header with Chat/Space toggle */}
          <div style={{background:"#fff",borderBottom:"1.5px solid #e0d9cf",padding:"8px 14px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:15,fontWeight:800,color:"#2c2420"}}>{GRP_ICONS[cur.subject]||"📚"} {cur.name}</div>
              <div style={{fontSize:10,color:"#9a9088"}}>Code: {cur.code} · {(cur.members||[]).length} member{(cur.members||[]).length!==1?"s":""}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
              {myFriends.length>0&&<button onClick={()=>setShowInviteFriends(true)} style={{padding:"5px 12px",borderRadius:99,background:"#f0edfb",border:"1.5px solid #c0b4e8",color:"#7a68c0",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer"}}>👤 Add Friend</button>}
              {/* Chat / Space toggle */}
              <div style={{display:"flex",background:"#f0edfb",borderRadius:99,padding:3,gap:2}}>
                <button onClick={()=>setActiveView("chat")} style={{padding:"5px 14px",borderRadius:99,background:activeView==="chat"?"#7a68c0":"transparent",color:activeView==="chat"?"#fff":"#7a68c0",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer",transition:"all .14s"}}>💬 Chat</button>
                <button onClick={()=>setActiveView("space")} style={{padding:"5px 14px",borderRadius:99,background:activeView==="space"?"#7a68c0":"transparent",color:activeView==="space"?"#fff":"#7a68c0",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer",transition:"all .14s"}}>📓 Space</button>
              </div>
            </div>
          </div>
          {/* Invite friends modal */}
          {showInviteFriends&&(
            <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowInviteFriends(false)}>
              <div style={{background:"#fff",borderRadius:16,padding:"22px 24px",width:340,maxWidth:"92vw",boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
                <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:4}}>Add Friend to Group</div>
                <div style={{fontSize:12,color:"#9a9088",marginBottom:16}}>Tap a friend to send them an invite to <strong>{cur.name}</strong></div>
                {myFriends.length===0&&<div style={{textAlign:"center",padding:20,color:"#9a9088",fontSize:13}}>No friends yet — add some in the Friends tab!</div>}
                <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto"}}>
                  {myFriends.map(f=>{
                    const already=(cur.members||[]).find(m=>m.uid===f.uid);
                    return(
                      <div key={f.id} onClick={!already?()=>inviteFriendToGroup(f):undefined} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:11,border:`1.5px solid ${already?"#e0d9cf":"#c0b4e8"}`,background:already?"#f6f3ee":"#f8f6ff",cursor:already?"default":"pointer",transition:"all .12s"}} onMouseEnter={e=>{if(!already)e.currentTarget.style.background="#f0edfb";}} onMouseLeave={e=>{if(!already)e.currentTarget.style.background="#f8f6ff";}}>
                        <Av name={f.name} size={36}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:700,color:"#2c2420"}}>{f.name}</div>
                          <div style={{fontSize:10,color:"#9a9088"}}>{f.code}</div>
                        </div>
                        {already?<span style={{fontSize:11,fontWeight:700,color:"#9a9088"}}>In group</span>:<span style={{fontSize:11,fontWeight:800,color:"#7a68c0"}}>Invite →</span>}
                      </div>
                    );
                  })}
                </div>
                <button onClick={()=>setShowInviteFriends(false)} style={{width:"100%",marginTop:14,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Close</button>
              </div>
            </div>
          )}

          {activeView==="chat"?(
            <div style={{flex:1,display:"flex",flexDirection:mob?"column":"row",overflow:"hidden",minHeight:0}}>
              {/* Thread sidebar — horizontal chips on mobile, vertical on desktop */}
              {mob?(
                <div style={{flexShrink:0,background:"#f6f4ff",borderBottom:"1.5px solid #e0d9cf",overflowX:"auto",padding:"6px 8px",display:"flex",gap:5,WebkitOverflowScrolling:"touch"}}>
                  {THREADS.map(t=><button key={t} onClick={()=>setActiveThread(t)} style={{flexShrink:0,padding:"5px 11px",borderRadius:99,border:`1.5px solid ${activeThread===t?"#a884ff":"#d0c8bb"}`,background:activeThread===t?"#a884ff":"#fff",color:activeThread===t?"#fff":"#5a5048",fontFamily:"'Nunito',sans-serif",fontSize:11,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap"}}>
                    <span>{THREAD_ICONS[t]||"💬"}</span><span style={{textTransform:"capitalize"}}>{t}</span>
                  </button>)}
                </div>
              ):(
                <div style={{width:130,flexShrink:0,background:"#f6f4ff",borderRight:"1.5px solid #e0d9cf",display:"flex",flexDirection:"column",overflow:"hidden"}}>
                  <div style={{padding:"8px 10px",fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",borderBottom:"1px solid #e0d9cf"}}>Threads</div>
                  <div style={{flex:1,overflowY:"auto",padding:"6px 6px"}}>
                    {THREADS.map(t=><div key={t} onClick={()=>setActiveThread(t)} style={{padding:"7px 9px",borderRadius:8,marginBottom:2,cursor:"pointer",background:activeThread===t?"rgba(168,132,255,.18)":"transparent",fontWeight:activeThread===t?800:600,fontSize:11,color:activeThread===t?"#7a68c0":"#5a5048",display:"flex",alignItems:"center",gap:5}} onMouseEnter={e=>e.currentTarget.style.background="rgba(168,132,255,.1)"} onMouseLeave={e=>e.currentTarget.style.background=activeThread===t?"rgba(168,132,255,.18)":"transparent"}>
                      <span>{THREAD_ICONS[t]||"💬"}</span><span style={{textTransform:"capitalize"}}>{t}</span>
                    </div>)}
                  </div>
                </div>
              )}
              {/* Messages */}
              <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
                <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
                  {threadMsgs.length===0&&<div style={{textAlign:"center",color:"#9a9088",fontStyle:"italic",paddingTop:30,fontSize:13}}>No messages in #{activeThread} yet. Say hello! 👋</div>}
                  {threadMsgs.map(msg=>{
                    const isMe=msg.uid===profile.id;
                    return(
                      <div key={msg.id} style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"flex-end",gap:7}}>
                        {!isMe&&<Av name={msg.name} size={26}/>}
                        <div style={{maxWidth:"72%"}}>
                          {!isMe&&<div style={{fontSize:10,fontWeight:800,color:"#9a9088",marginBottom:3,marginLeft:2}}>{msg.name}</div>}
                          <div style={{padding:"9px 13px",borderRadius:isMe?"16px 16px 4px 16px":"16px 16px 16px 4px",background:isMe?"linear-gradient(135deg,#a884ff,#7a68c0)":"#fff",color:isMe?"#fff":"#2c2420",fontSize:13,lineHeight:1.45,boxShadow:"0 1px 4px rgba(0,0,0,.08)",border:isMe?"none":"1.5px solid #e0d9cf"}}>
                            {msg.spaceCode&&<div style={{fontSize:10,color:isMe?"rgba(255,255,255,.7)":"#9a9088",marginBottom:3}}>📓 Study Space</div>}
                            {msg.text}
                          </div>
                          <div style={{fontSize:9,color:"#b0a898",marginTop:3,textAlign:isMe?"right":"left"}}>{new Date(msg.at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={msgEndRef}/>
                </div>
                {/* Space share panel */}
                {showSpacePanel&&spaces.length>0&&(
                  <div style={{padding:"8px 14px",background:"#f6f4ff",borderTop:"1.5px solid #e0d9cf"}}>
                    <div style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:7}}>Share a Study Space</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {spaces.map(sp=><button key={sp.id} onClick={()=>shareSpace(sp)} style={{fontSize:11,fontWeight:700,padding:"5px 11px",borderRadius:99,border:"1.5px solid #c0b4e8",background:"#fff",color:"#7a68c0",cursor:"pointer"}}>📓 {sp.name}</button>)}
                    </div>
                  </div>
                )}
                {showSpacePanel&&spaces.length===0&&(
                  <div style={{padding:"8px 14px",background:"#f6f4ff",borderTop:"1.5px solid #e0d9cf"}}>
                    <div style={{fontSize:12,color:"#9a9088",fontStyle:"italic"}}>No spaces yet — create one in the 📓 Spaces sidebar to share here.</div>
                  </div>
                )}
                {/* Input */}
                <div style={{padding:"10px 14px",borderTop:"1.5px solid #e0d9cf",background:"#fff"}}>
                  {activeThread&&<div style={{fontSize:11,color:"#9a9088",marginBottom:5}}>Posting in <strong style={{color:"#7a68c0"}}>#{activeThread}</strong></div>}
                  <div style={{display:"flex",alignItems:"center",gap:6,background:"#f6f4ff",border:"1.5px solid #c0b4e8",borderRadius:99,padding:"6px 8px 6px 14px"}}>
                    <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage(input);}}} placeholder={`Message #${activeThread}…`} style={{flex:1,background:"transparent",border:"none",outline:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,color:"#2c2420",caretColor:"#a884ff"}}/>
                    <button onClick={()=>setShowSpacePanel(p=>!p)} title="Share study space" style={{width:30,height:30,borderRadius:99,border:`1.5px solid ${showSpacePanel?"#a884ff":"transparent"}`,background:showSpacePanel?"#f0edfb":"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>📓</button>
                    <button onClick={()=>fileRef.current?.click()} style={{width:30,height:30,borderRadius:99,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>📎</button>
                    <input ref={fileRef} type="file" style={{display:"none"}} onChange={e=>{if(e.target.files[0])sendFile(e.target.files[0]);}}/>
                    <button onClick={()=>sendMessage(input)} disabled={sending||!input.trim()} style={{width:32,height:32,borderRadius:99,background:input.trim()?"linear-gradient(135deg,#a884ff,#7a68c0)":"#e0d9cf",color:"#fff",border:"none",cursor:input.trim()?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>↑</button>
                  </div>
                </div>
              </div>
            </div>
          ):(
            /* ── Integrated Group Space (Notes + Canvas) ── */
            <div style={{flex:1,display:"flex",flexDirection:mob?"column":"row",overflow:"hidden",minHeight:0}}>
              {/* Mobile tab switcher */}
              {mob&&(
                <div style={{flexShrink:0,background:"#fff",borderBottom:"1.5px solid #e0d9cf",display:"flex",padding:"6px 10px",gap:6}}>
                  <button onClick={()=>setActiveView("chat")} style={{flex:1,padding:"6px 0",borderRadius:99,background:activeView!=="space"?"#7a68c0":"transparent",color:activeView!=="space"?"#fff":"#9a9088",border:`1.5px solid ${activeView!=="space"?"#7a68c0":"#d0c8bb"}`,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>📄 Notes</button>
                  <button onClick={()=>setActiveView("space")} style={{flex:1,padding:"6px 0",borderRadius:99,background:activeView==="space"?"#7a68c0":"transparent",color:activeView==="space"?"#fff":"#9a9088",border:`1.5px solid ${activeView==="space"?"#7a68c0":"#d0c8bb"}`,fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer"}}>🖌 Canvas</button>
                </div>
              )}
              {/* Notes panel */}
              {(!mob||activeView!=="space")&&<div style={{width:mob?"100%":280,flexShrink:0,display:"flex",flexDirection:"column",borderRight:mob?"none":"1.5px solid #e0d9cf",background:"#fff"}}>
                <div style={{padding:"9px 13px",borderBottom:"1.5px solid #e0d9cf",fontSize:12,fontWeight:800,color:"#7a68c0"}}>📄 Group Notes</div>
                <textarea
                  placeholder="Shared notes for this group… (type here together)"
                  style={{flex:1,padding:"13px 15px",fontFamily:"'Nunito',sans-serif",fontSize:13,lineHeight:1.9,color:"#2c2420",background:"transparent",border:"none",outline:"none",resize:"none",backgroundImage:"repeating-linear-gradient(transparent,transparent 27px,#ece8e1 27px,#ece8e1 28px)",backgroundAttachment:"local"}}
                />
              </div>}
              {/* Canvas panel */}
              {(!mob||activeView==="space")&&<div style={{flex:1,display:"flex",flexDirection:"column",background:"#f6f3ee",minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5,padding:"6px 11px",background:"#fff",borderBottom:"1.5px solid #e0d9cf",flexWrap:"wrap",flexShrink:0}}>
                  <div style={{fontSize:11,fontWeight:800,color:"#7a68c0",marginRight:4}}>🖌 Group Whiteboard</div>
                  <div style={{display:"flex",gap:3}}>{[["pencil","✏️"],["marker","🖊️"],["highlighter","🖌️"],["eraser","🧹"]].map(([t,ic])=><button key={t} onClick={()=>setTool(t)} style={{width:28,height:28,borderRadius:6,border:`1.5px solid ${tool===t?"#c0b4e8":"transparent"}`,background:tool===t?"#f0edfb":"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>{ic}</button>)}</div>
                  <div style={{width:1,height:18,background:"#d0c8bb"}}/>
                  <div style={{display:"flex",gap:4}}>{SWCOLS.map((c,i)=><div key={i} onClick={()=>setColor(c)} style={{width:18,height:18,borderRadius:"50%",background:c,cursor:"pointer",border:`2px solid ${color===c?"#2c2420":"rgba(0,0,0,.1)"}`,transform:color===c?"scale(1.15)":"none"}}/>)}</div>
                  <div style={{width:1,height:18,background:"#d0c8bb"}}/>
                  <input type="range" min={1} max={30} value={sz} onChange={e=>setSz(+e.target.value)} style={{width:55,accentColor:"#7a68c0"}}/>
                  <div style={{marginLeft:"auto",display:"flex",gap:4}}>
                    <button onClick={undoC} style={{fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>↩ Undo</button>
                    <button onClick={clearC} style={{fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:99,border:"1.5px solid #d0c8bb",background:"transparent",color:"#9a9088",cursor:"pointer"}}>Clear</button>
                  </div>
                </div>
                <div style={{flex:1,position:"relative",overflow:"hidden",minHeight:0}}>
                  <canvas ref={canvasRef} style={{position:"absolute",inset:0,cursor:"crosshair",touchAction:"none"}}
                    onMouseDown={e=>{const p=getXY(e);startD(p.x,p.y);}}
                    onMouseMove={e=>{const p=getXY(e);doD(p.x,p.y);}}
                    onMouseUp={endD} onMouseLeave={endD}
                    onTouchStart={e=>{e.preventDefault();const t=e.touches[0],r=canvasRef.current.getBoundingClientRect();startD(t.clientX-r.left,t.clientY-r.top);}}
                    onTouchMove={e=>{e.preventDefault();const t=e.touches[0],r=canvasRef.current.getBoundingClientRect();doD(t.clientX-r.left,t.clientY-r.top);}}
                    onTouchEnd={endD}
                  />
                </div>
              </div>}
            </div>
          )}
        </div>
      )}

      {/* Create group modal */}
      {showCreate&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowCreate(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:360,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:14}}>Create Study Group</div>
            <input value={newGrp.name} onChange={e=>setNewGrp(p=>({...p,name:e.target.value}))} autoFocus placeholder="Group name…" style={{width:"100%",background:"#f6f3ee",border:"1.5px solid #c0b4e8",borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:10}}/>
            <select value={newGrp.subject} onChange={e=>setNewGrp(p=>({...p,subject:e.target.value}))} style={{width:"100%",background:"#f6f3ee",border:"1.5px solid #e0d9cf",borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:14}}>
              {Object.entries(SN).map(([k,v])=><option key={k} value={k}>{GRP_ICONS[k]||"📚"} {v}</option>)}
            </select>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCreate(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={createGroup} style={{flex:1,padding:10,borderRadius:99,background:"#7a68c0",color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Create</button>
            </div>
          </div>
        </div>
      )}
      {showJoin&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowJoin(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:340,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:6}}>Join a Study Group</div>
            <div style={{fontSize:12,color:"#9a9088",marginBottom:14}}>Enter the 7-character group code</div>
            <input value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&joinGroup()} autoFocus placeholder="e.g. A4BX9R2" style={{width:"100%",background:"#f6f3ee",border:"1.5px solid #c0b4e8",borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:18,fontWeight:700,color:"#2c2420",outline:"none",marginBottom:14,letterSpacing:3,textAlign:"center"}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowJoin(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={joinGroup} style={{flex:1,padding:10,borderRadius:99,background:"#7a68c0",color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Join →</button>
            </div>
          </div>
        </div>
      )}
      {/* end sectionView===groups ternary */}
      </>
      )}

      {/* Create Space modal — always available regardless of sectionView */}
      {showCreateSpace&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowCreateSpace(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:340,boxShadow:"0 8px 32px rgba(42,36,32,.15)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:14}}>New Study Space</div>
            <input value={newSpaceName} onChange={e=>setNewSpaceName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&createSpace()} autoFocus placeholder="Space name…" style={{width:"100%",background:"#f6f3ee",border:"1.5px solid #c0b4e8",borderRadius:9,padding:"9px 12px",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:600,color:"#2c2420",outline:"none",marginBottom:14}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCreateSpace(false)} style={{flex:1,padding:10,borderRadius:99,background:"#f6f3ee",border:"1.5px solid #e0d9cf",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:700,color:"#5a5048",cursor:"pointer"}}>Cancel</button>
              <button onClick={createSpace} style={{flex:1,padding:10,borderRadius:99,background:"#7a68c0",color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer"}}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI TUTOR ────────────────────────────────────────────────────
function AiTutor({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const assignments=JSON.parse(localStorage.getItem("sd_a_"+profile.id)||"[]").filter(x=>!x.done);
  const [selected,setSelected]=useState(null);
  const [showPicker,setShowPicker]=useState(false);
  const [messages,setMessages]=useState([]);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(false);
  const [tutorStarted,setTutorStarted]=useState(false);
  const msgEndRef=useRef(null);
  const inputRef=useRef(null);
  useEffect(()=>{msgEndRef.current?.scrollIntoView({behavior:"smooth"});},[messages]);

  async function startSession(assign){
    setSelected(assign);
    setShowPicker(false);
    setTutorStarted(true);
    setLoading(true);
    const noteCtx=assign.detailedNote?`\nAssignment details provided by student: ${assign.detailedNote}`:"";
    const prompt=`You are a helpful, encouraging AI tutor helping a student with their assignment.

Assignment: "${assign.title}"
Type: ${assign.type}
Subject: ${SN[assign.subj]||assign.subj}
Due: ${assign.due}${noteCtx}

Start by introducing yourself warmly and asking the student what they've done so far or what part is confusing them. Keep your response short and conversational. Don't give away all the answers — guide them step by step through questions and hints. Be encouraging and patient.`;
    const response=await callClaude([{role:"user",content:prompt}]);
    setMessages([{role:"assistant",content:response}]);
    setLoading(false);
    setTimeout(()=>inputRef.current?.focus(),100);
  }

  async function callClaude(msgs){
    try{
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:600,system:`You are a friendly, encouraging AI tutor helping a student complete their assignment step by step. Guide them through with questions and hints rather than giving direct answers. Be warm, patient, and specific. Keep responses concise (2-4 sentences usually). When they seem stuck, offer a more direct hint. When they're making progress, celebrate it.`,messages:msgs})});
      const data=await r.json();
      return data.content?.map(c=>c.text||"").join("")||"Sorry, I had trouble responding. Try again!";
    }catch{return "I'm having connection issues. Please try again.";}
  }

  async function sendMessage(){
    if(!input.trim()||loading||!selected)return;
    const userMsg={role:"user",content:input.trim()};
    const newMsgs=[...messages,userMsg];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    // Build context for Claude
    const apiMsgs=newMsgs.map(m=>({role:m.role,content:m.content}));
    const response=await callClaude(apiMsgs);
    setMessages(p=>[...p,{role:"assistant",content:response}]);
    setLoading(false);
    setTimeout(()=>inputRef.current?.focus(),100);
  }

  return(
    <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0,fontFamily:"'Nunito',sans-serif",flexDirection:"column"}}>
      {/* Header */}
      <div style={{padding:"12px 18px",background:"#fff",borderBottom:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${a.main},${a.deep})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🤖</div>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Lora',serif",fontSize:16,fontWeight:600,color:"#2c2420"}}>AI Tutor</div>
          <div style={{fontSize:11,color:"#9a9088"}}>{selected?`Helping with: ${selected.title}`:"Choose an assignment to get started"}</div>
        </div>
        <button onClick={()=>setShowPicker(true)} style={{padding:"7px 16px",borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:12,fontWeight:800,cursor:"pointer",boxShadow:`0 2px 8px ${a.main}44`}}>
          {selected?"Switch Assignment":"Help with Assignment →"}
        </button>
      </div>

      {/* Assignment picker modal */}
      {showPicker&&(
        <div style={{position:"fixed",inset:0,background:"rgba(44,36,32,.4)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowPicker(false)}>
          <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",width:460,maxWidth:"92vw",maxHeight:"80vh",boxShadow:"0 8px 32px rgba(42,36,32,.15)",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontFamily:"'Lora',serif",fontSize:17,fontWeight:600,color:"#2c2420",marginBottom:6}}>Which assignment do you need help with?</div>
            <div style={{fontSize:12,color:"#9a9088",marginBottom:16}}>The AI tutor will guide you through it step by step.</div>
            <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:7}}>
              {assignments.length===0?(
                <div style={{textAlign:"center",padding:24,color:"#9a9088",fontStyle:"italic"}}>No active assignments found. Add some in the Planner first! ✨</div>
              ):assignments.map(a2=>{
                const sc=SCOL[a2.subj]||SCOL.oth;
                const ds=dueStr(a2);
                return(
                  <div key={a2.id} onClick={()=>startSession(a2)} style={{padding:"12px 14px",borderRadius:11,border:`1.5px solid ${selected?.id===a2.id?a.main:"#e0d9cf"}`,background:selected?.id===a2.id?a.light:"#faf8f4",cursor:"pointer",transition:"all .13s"}} onMouseEnter={e=>e.currentTarget.style.background=a.light} onMouseLeave={e=>e.currentTarget.style.background=selected?.id===a2.id?a.light:"#faf8f4"}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:16}}>{({hw:"📝",essay:"✍️",exam:"📖",proj:"🗂️",read:"📚",lab:"🔬",oth:"📌"}[a2.type])||"📌"}</span>
                      <span style={{fontSize:13,fontWeight:700,color:"#2c2420",flex:1}}>{a2.title}</span>
                      <span style={{fontSize:11,fontWeight:600,color:ds.c}}>{ds.t}</span>
                    </div>
                    <div style={{display:"flex",gap:5}}>
                      <span style={{fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:99,border:`1.5px solid ${sc.bd}`,color:sc.c,background:sc.bg}}>{SN[a2.subj]}</span>
                      <span style={{fontSize:10,padding:"2px 7px",borderRadius:99,border:"1.5px solid #e0d9cf",color:"#9a9088",textTransform:"capitalize"}}>{a2.type}</span>
                      {a2.detailedNote&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:99,background:"#edf5f0",border:"1.5px solid #a8ccb4",color:"#3a6048"}}>📋 Has details</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!tutorStarted?(
        <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,padding:24}}>
          <div style={{fontSize:60,opacity:.2}}>🤖</div>
          <div style={{fontFamily:"'Lora',serif",fontSize:22,fontWeight:600,color:"#5a5048",textAlign:"center"}}>Your AI Tutor is Ready</div>
          <div style={{fontSize:13,color:"#9a9088",textAlign:"center",maxWidth:340,lineHeight:1.6}}>I'll guide you through your assignment step by step with hints and questions — not just answers. You'll actually learn it!</div>
          <button onClick={()=>setShowPicker(true)} style={{marginTop:8,padding:"12px 28px",borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:800,cursor:"pointer",boxShadow:`0 4px 14px ${a.main}55`}}>
            Help with Assignment →
          </button>
          {assignments.length>0&&(
            <div style={{marginTop:4,fontSize:12,color:"#9a9088"}}>{assignments.length} assignment{assignments.length!==1?"s":""} available</div>
          )}
        </div>
      ):(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}>
          {/* Assignment context banner */}
          {selected&&(
            <div style={{padding:"8px 16px",background:a.light,borderBottom:`1.5px solid ${a.mid}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              <span style={{fontSize:14}}>{({hw:"📝",essay:"✍️",exam:"📖",proj:"🗂️",read:"📚",lab:"🔬",oth:"📌"}[selected.type])||"📌"}</span>
              <span style={{fontSize:12,fontWeight:700,color:a.deep,flex:1}}>{selected.title}</span>
              <span style={{fontSize:11,color:a.main}}>{SN[selected.subj]} · {dueStr(selected).t}</span>
            </div>
          )}
          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:12}}>
            {messages.map((msg,i)=>{
              const isUser=msg.role==="user";
              return(
                <div key={i} style={{display:"flex",flexDirection:isUser?"row-reverse":"row",alignItems:"flex-end",gap:9}}>
                  {!isUser&&<div style={{width:30,height:30,borderRadius:"50%",background:`linear-gradient(135deg,${a.main},${a.deep})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>🤖</div>}
                  <div style={{maxWidth:"78%"}}>
                    <div style={{padding:"11px 15px",borderRadius:isUser?"16px 16px 4px 16px":"16px 16px 16px 4px",background:isUser?`linear-gradient(135deg,${a.main},${a.deep})`:"#fff",color:isUser?"#fff":"#2c2420",fontSize:13,lineHeight:1.55,boxShadow:"0 1px 4px rgba(0,0,0,.08)",border:isUser?"none":`1.5px solid ${a.mid}`,whiteSpace:"pre-wrap"}}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              );
            })}
            {loading&&(
              <div style={{display:"flex",alignItems:"flex-end",gap:9}}>
                <div style={{width:30,height:30,borderRadius:"50%",background:`linear-gradient(135deg,${a.main},${a.deep})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>🤖</div>
                <div style={{padding:"11px 15px",borderRadius:"16px 16px 16px 4px",background:"#fff",border:`1.5px solid ${a.mid}`,color:"#9a9088",fontSize:13,fontStyle:"italic"}}>Thinking…</div>
              </div>
            )}
            <div ref={msgEndRef}/>
          </div>
          {/* Input */}
          <div style={{padding:"10px 16px",borderTop:"1.5px solid #e0d9cf",background:"#fff",flexShrink:0}}>
            <div style={{display:"flex",gap:8,alignItems:"center",background:a.light,border:`1.5px solid ${a.mid}`,borderRadius:99,padding:"6px 8px 6px 16px"}}>
              <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}}} placeholder="Ask your tutor a question or share what you've done so far…" style={{flex:1,background:"transparent",border:"none",outline:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,color:"#2c2420",caretColor:a.main}} disabled={loading}/>
              <button onClick={sendMessage} disabled={!input.trim()||loading} style={{width:34,height:34,borderRadius:99,background:input.trim()&&!loading?`linear-gradient(135deg,${a.main},${a.deep})`:"#e0d9cf",color:"#fff",border:"none",cursor:input.trim()&&!loading?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>↑</button>
            </div>
            <div style={{fontSize:10,color:"#b0a898",textAlign:"center",marginTop:6}}>The AI tutor guides you with hints — not just answers. You've got this! 💪</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FRIENDS ──────────────────────────────────────────────────────
function Friends({profile,accent}){
  const a=ACC[accent]||ACC.gold;
  const {mob}=useWindowSize();
  const storKey="sd_friends_"+profile.id;
  const [friends,setFriends]=useState(()=>JSON.parse(localStorage.getItem(storKey)||"[]"));
  const [tab,setTab]=useState("friends");
  const [addMode,setAddMode]=useState("code");
  const [addInput,setAddInput]=useState("");
  const [addMsg,setAddMsg]=useState("");
  const [sideOpen,setSideOpen]=useState(!mob);
  const myCode=profile.id.slice(-7).toUpperCase();
  useEffect(()=>{localStorage.setItem(storKey,JSON.stringify(friends));},[friends,storKey]);

  useEffect(()=>{
    supabase
      .from("users")
      .upsert({ friend_code: myCode, uid: profile.id, name: profile.name, updated_at: new Date().toISOString() })
      .catch(() => {});
  },[myCode,profile.id,profile.name]);

  // Listen for incoming friend requests in real time
  useEffect(()=>{
    const handleRequests = (requests) => {
      setFriends(prev => {
        let updated = [...prev];
        for (const [fromCode, req] of Object.entries(requests || {})) {
          if (!updated.find(f => f.code === fromCode)) {
            updated = [...updated, { id: req.sentAt, name: req.fromName, code: fromCode, uid: req.fromUid, status: "pending", direction: "incoming", addedAt: req.sentAt }];
          }
        }
        return updated;
      });
    };

    const loadRequests = async () => {
      const { data } = await supabase
        .from("friend_requests")
        .select("requests")
        .eq("friend_code", myCode)
        .maybeSingle();
      if (data?.requests) {
        handleRequests(data.requests);
      }
    };
    loadRequests();

    const channel = supabase
      .channel(`friend_requests_${myCode}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "friend_requests", filter: `friend_code=eq.${myCode}` },
        (payload) => {
          if (payload.new && payload.new.requests) {
            handleRequests(payload.new.requests);
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },[myCode]);

  async function addFriend(){
    if(!addInput.trim()){setAddMsg("Enter a friend code.");return;}
    const code=addInput.trim().toUpperCase();
    if(code===myCode){setAddMsg("That's your own code!");return;}
    if(friends.find(f=>f.code===code)){setAddMsg("Already friends with this person!");return;}
    
    try {
      const { data: userData, error } = await supabase
        .from("users")
        .select("uid, name")
        .eq("friend_code", code)
        .maybeSingle();
      if (error || !userData) { setAddMsg("User not found. Check the code."); return; }
      
      const { data } = await supabase
        .from("friend_requests")
        .select("requests")
        .eq("friend_code", code)
        .maybeSingle();
      
      const currentReqs = data?.requests || {};
      const newReqs = {
        ...currentReqs,
        [myCode]: { fromName: profile.name, fromUid: profile.id, sentAt: Date.now() }
      };
      
      await supabase
        .from("friend_requests")
        .upsert({ friend_code: code, requests: newReqs, updated_at: new Date().toISOString() });
      
      setFriends(p => [...p, { id: Date.now(), name: userData.name, code, uid: userData.uid, status: "pending", direction: "outgoing", addedAt: Date.now() }]);
      setAddMsg("Friend request sent! ✓"); setAddInput(""); setTimeout(() => setAddMsg(""), 2500);
    } catch {
      setAddMsg("An error occurred. Try again.");
    }
  }
  async function acceptFriend(f){
    try {
      const { data } = await supabase
        .from("friend_requests")
        .select("requests")
        .eq("friend_code", myCode)
        .maybeSingle();
      if (data?.requests) {
        const updatedReqs = { ...data.requests };
        delete updatedReqs[f.code];
        await supabase
          .from("friend_requests")
          .upsert({ friend_code: myCode, requests: updatedReqs, updated_at: new Date().toISOString() });
      }
    } catch {}
    setFriends(p=>p.map(x=>x.id===f.id?{...x,status:"accepted"}:x));
  }
  function removeFriend(id){setFriends(p=>p.filter(f=>f.id!==id));}
  const incoming=friends.filter(f=>f.status==="pending"&&f.direction==="incoming");
  const accepted=friends.filter(f=>f.status==="accepted");
  const outgoing=friends.filter(f=>f.status==="pending"&&f.direction!=="incoming");
  const SAC="#a884ff";

  return(
    <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0,fontFamily:"'Nunito',sans-serif",position:"relative"}}>
      {/* Sidebar toggle on mobile */}
      {mob&&<button onClick={()=>setSideOpen(p=>!p)} style={{position:"absolute",top:10,left:10,zIndex:20,background:"#1c1a2e",border:"none",borderRadius:8,color:SAC,fontSize:18,padding:"6px 10px",cursor:"pointer"}}>{sideOpen?"✕":"☰"}</button>}
      {/* Sidebar */}
      {(!mob||sideOpen)&&(
        <div style={{width:mob?"100%":240,flexShrink:0,background:"#1c1a2e",display:"flex",flexDirection:"column",overflow:"hidden",position:mob?"absolute":"relative",inset:mob?"0":undefined,zIndex:mob?10:undefined}}>
          <div style={{padding:"14px 14px 10px",borderBottom:"1px solid rgba(255,255,255,.08)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontFamily:"'Lora',serif",fontSize:15,fontWeight:600,color:"#fff"}}>👥 Friends</div>
            {mob&&<button onClick={()=>setSideOpen(false)} style={{background:"none",border:"none",color:"rgba(255,255,255,.5)",fontSize:18,cursor:"pointer"}}>✕</button>}
          </div>
          <div style={{padding:"10px 10px",flex:1,overflowY:"auto"}}>
            <div style={{background:"rgba(168,132,255,.1)",border:"1px solid rgba(168,132,255,.2)",borderRadius:10,padding:"11px 12px",marginBottom:12}}>
              <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"rgba(168,132,255,.7)",marginBottom:5}}>Your Friend Code</div>
              <div style={{fontFamily:"monospace",fontSize:18,fontWeight:800,color:SAC,letterSpacing:3}}>{myCode}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:3}}>Share this to add friends</div>
            </div>
            {[["friends","Friends",accepted.length+outgoing.length],["incoming","Requests",incoming.length]].map(([k,l,n])=>(
              <div key={k} onClick={()=>{setTab(k);if(mob)setSideOpen(false);}} style={{padding:"8px 10px",borderRadius:8,marginBottom:3,cursor:"pointer",background:tab===k?"rgba(168,132,255,.15)":"transparent",borderLeft:tab===k?`3px solid ${SAC}`:"3px solid transparent",display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.06)"} onMouseLeave={e=>e.currentTarget.style.background=tab===k?"rgba(168,132,255,.15)":"transparent"}>
                <span style={{fontSize:12,fontWeight:700,color:tab===k?SAC:"#fff"}}>{l}</span>
                {n>0&&<span style={{fontSize:10,fontWeight:800,background:SAC,color:"#fff",borderRadius:99,padding:"1px 7px"}}>{n}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Main */}
      <div style={{flex:1,overflowY:"auto",padding:mob?"12px":"18px",display:"flex",flexDirection:"column",gap:12}}>
        {/* Incoming requests banner — always visible when requests exist */}
        {incoming.length>0&&(
          <div style={{background:"#f0edfb",border:`1.5px solid #c0b4e8`,borderRadius:13,padding:"12px 14px"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#4a38a0",marginBottom:10}}>🔔 Friend Requests ({incoming.length})</div>
            {incoming.map(f=>(
              <div key={f.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:"#fff",borderRadius:10,padding:"10px 12px",border:"1.5px solid #e0d9cf"}}>
                <Av name={f.name} size={36}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#2c2420",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                  <div style={{fontSize:10,color:"#9a9088"}}>Code: {f.code}</div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>acceptFriend(f)} style={{fontSize:12,fontWeight:800,padding:"7px 14px",borderRadius:99,background:"#5a8a6a",color:"#fff",border:"none",cursor:"pointer"}}>Accept</button>
                  <button onClick={()=>removeFriend(f.id)} style={{fontSize:12,fontWeight:800,padding:"7px 12px",borderRadius:99,border:"1.5px solid #e0d9cf",background:"transparent",color:"#9a9088",cursor:"pointer"}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Add friend */}
        <div style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:13,padding:"14px 16px"}}>
          <div style={{fontSize:12,fontWeight:800,color:"#2c2420",marginBottom:11}}>➕ Add Friend</div>
          <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
            {[["code","By Friend Code"],["qr","QR Code"]].map(([k,l])=>(
              <button key={k} onClick={()=>setAddMode(k)} style={{fontSize:11,fontWeight:700,padding:"5px 12px",borderRadius:99,border:`1.5px solid ${addMode===k?a.main:"#d0c8bb"}`,background:addMode===k?a.light:"#fff",color:addMode===k?a.deep:"#5a5048",cursor:"pointer"}}>{l}</button>
            ))}
          </div>
          {addMode==="qr"?(
            <div style={{textAlign:"center",padding:"20px",background:"#f6f3ee",borderRadius:10,color:"#9a9088",fontSize:13}}>📷 Camera access coming soon</div>
          ):(
            <div style={{display:"flex",gap:8}}>
              <input value={addInput} onChange={e=>setAddInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addFriend()} placeholder="Enter friend code (e.g. A4BX9R2)…" style={{flex:1,background:"#f6f3ee",border:`1.5px solid ${a.mid}`,borderRadius:9,padding:"10px 12px",fontFamily:"'Nunito',sans-serif",fontSize:14,fontWeight:600,color:"#2c2420",outline:"none",minWidth:0}}/>
              <button onClick={addFriend} style={{padding:"10px 18px",borderRadius:99,background:a.main,color:"#fff",border:"none",fontFamily:"'Nunito',sans-serif",fontSize:13,fontWeight:800,cursor:"pointer",flexShrink:0}}>Add</button>
            </div>
          )}
          {addMsg&&<div style={{marginTop:8,fontSize:12,fontWeight:700,color:"#5a8a6a"}}>{addMsg}</div>}
        </div>
        {/* Friends list */}
        {tab==="friends"&&(
          <div>
            <div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:10}}>Friends ({accepted.length})</div>
            {accepted.length===0&&outgoing.length===0&&<div style={{textAlign:"center",padding:24,color:"#9a9088",fontStyle:"italic"}}>No friends yet. Add someone using their friend code! 👆</div>}
            {outgoing.length>0&&<div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#c8882a",marginBottom:8,marginTop:4}}>Sent ({outgoing.length})</div>}
            <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fill,minmax(${mob?"140px":"200px"},1fr))`,gap:9,marginBottom:outgoing.length>0?10:0}}>
              {outgoing.map(f=>(
                <div key={f.id} style={{background:"#fffbf0",border:"1.5px solid #e8c48a",borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
                  <Av name={f.name} size={32}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#2c2420",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                    <div style={{fontSize:10,fontWeight:700,color:"#c8882a",marginTop:2}}>⏳ Pending</div>
                  </div>
                  <button onClick={()=>removeFriend(f.id)} style={{width:22,height:22,borderRadius:6,border:"1.5px solid #e0d9cf",background:"transparent",cursor:"pointer",fontSize:11,color:"#9a9088"}} onMouseEnter={e=>{e.currentTarget.style.background="#fdf0f0";e.currentTarget.style.color="#d04848";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#9a9088";}}>✕</button>
                </div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fill,minmax(${mob?"140px":"200px"},1fr))`,gap:9}}>
              {accepted.map(f=>(
                <div key={f.id} style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
                  <Av name={f.name} size={36}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#2c2420",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
                    <div style={{fontSize:10,color:"#9a9088",marginTop:1}}>{f.code}</div>
                    <div style={{fontSize:10,fontWeight:700,color:"#5a8a6a",marginTop:2}}>✓ Friends</div>
                  </div>
                  <button onClick={()=>removeFriend(f.id)} style={{width:24,height:24,borderRadius:6,border:"1.5px solid #e0d9cf",background:"transparent",cursor:"pointer",fontSize:11,color:"#9a9088"}} onMouseEnter={e=>{e.currentTarget.style.background="#fdf0f0";e.currentTarget.style.color="#d04848";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#9a9088";}}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab==="incoming"&&(
          <div>
            <div style={{fontSize:11,fontWeight:800,textTransform:"uppercase",letterSpacing:".8px",color:"#9a9088",marginBottom:10}}>Incoming Requests ({incoming.length})</div>
            {incoming.length===0&&<div style={{textAlign:"center",padding:24,color:"#9a9088",fontStyle:"italic"}}>No pending requests.</div>}
            {incoming.map(f=>(
              <div key={f.id} style={{background:"#fff",border:"1.5px solid #e0d9cf",borderRadius:11,padding:"11px 14px",display:"flex",alignItems:"center",gap:10,marginBottom:7}}>
                <Av name={f.name} size={34}/>
                <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:700,color:"#2c2420"}}>{f.name}</div><div style={{fontSize:10,color:"#9a9088",marginTop:1}}>Code: {f.code}</div></div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>acceptFriend(f)} style={{fontSize:11,fontWeight:800,padding:"6px 13px",borderRadius:99,background:"#5a8a6a",color:"#fff",border:"none",cursor:"pointer"}}>Accept</button>
                  <button onClick={()=>removeFriend(f.id)} style={{fontSize:11,fontWeight:800,padding:"6px 11px",borderRadius:99,border:"1.5px solid #e0d9cf",background:"transparent",color:"#9a9088",cursor:"pointer"}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────
export default function App(){
  const [profile,setProfile]=useState(null);
  const [needsOnboarding,setNeedsOnboarding]=useState(false);
  const [tab,setTab]=useState("planner");
  const [showSettings,setShowSettings]=useState(false);
  const [now,setNow]=useState(new Date());
  useEffect(()=>{const iv=setInterval(()=>{setNow(new Date());},2000);return()=>clearInterval(iv);},[]);

  // Listen to Supabase Session on Mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        handleUser(session.user);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        handleUser(session.user);
      } else {
        setProfile(null);
      }
    });

    async function handleUser(user) {
      const myCode = user.id.slice(-7).toUpperCase();
      const { data: existingUser } = await supabase
        .from("users")
        .select("friend_code")
        .eq("uid", user.id)
        .maybeSingle();

      if (!existingUser) {
        await supabase.from("users").upsert({
          friend_code: myCode,
          uid: user.id,
          name: user.user_metadata.name || user.user_metadata.full_name || "Student",
          username: user.user_metadata.username || "",
          email: user.email || "",
          updated_at: new Date().toISOString()
        });
      }

      onAuth(mapSupabaseUser(user));
    }

    return () => subscription.unsubscribe();
  }, []);

  function onAuth(acc){
    if(!acc.role){setProfile(acc);setNeedsOnboarding(true);}
    else setProfile(acc);
  }
  async function finishOnboarding({role,accent,bg}){
    const { data: { user }, error } = await supabase.auth.updateUser({
      data: { role, accent, bg }
    });
    if (!error && user) {
      setProfile(mapSupabaseUser(user));
      setNeedsOnboarding(false);
    }
  }
  async function updateProf(updates){
    const { data: { user }, error } = await supabase.auth.updateUser({
      data: updates
    });
    if (!error && user) {
      if (updates.name || updates.username) {
        const myCode = user.id.slice(-7).toUpperCase();
        await supabase.from("users").upsert({
          friend_code: myCode,
          uid: user.id,
          name: updates.name || user.user_metadata.name || "Student",
          username: updates.username || user.user_metadata.username || "",
          email: user.email || "",
          updated_at: new Date().toISOString()
        });
      }
      setProfile(mapSupabaseUser(user));
    }
  }
  function switchAccount(acc){
    signOut();
  }
  async function signOut(){
    await supabase.auth.signOut();
    setProfile(null);setShowSettings(false);
  }
  function newAccount(){
    signOut();
  }
  async function deleteAccount(uid){
    ["sd_a_","sd_ws_","sd_cls_","sd_friends_","sd_sg_"].forEach(k=>localStorage.removeItem(k+uid));
    await supabase.auth.signOut();
    setProfile(null);setShowSettings(false);
  }

  const {mob,tab:isTablet}=useWindowSize();

  if(!profile)return <Auth onAuth={onAuth}/>;
  if(needsOnboarding)return <Onboarding profile={profile} onDone={finishOnboarding}/>;

  const a=ACC[profile.accent]||ACC.gold;
  const bg=BGMAP[profile.bg]||"#f6f3ee";
  const hh=(now.getHours()%12||12).toString().padStart(2,"0"),mm=String(now.getMinutes()).padStart(2,"0"),ss2=String(now.getSeconds()).padStart(2,"0"),ap=now.getHours()>=12?"PM":"AM";
  const DAYS=["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const TABS=[
    {k:"planner",i:"📋",l:"Planner"},
    {k:"classroom",i:"🏫",l:"Classroom"},
    {k:"groups",i:"📚",l:"Groups"},
    {k:"tutor",i:"🤖",l:"AI Tutor"},
    {k:"friends",i:"👥",l:"Friends"},
  ];

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100vh",background:bg,fontFamily:"'Nunito',sans-serif",overflow:"hidden"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&family=Lora:wght@500;600&display=swap');*{box-sizing:border-box;margin:0;padding:0;}::-webkit-scrollbar{width:4px;height:4px;}::-webkit-scrollbar-thumb{background:#d0c8bb;border-radius:99px;}@keyframes spin{to{transform:rotate(360deg)}}input,textarea,select{font-size:16px !important;}@media(min-width:640px){input,textarea,select{font-size:inherit !important;}}`}</style>
      {/* Topbar */}
      <div style={{height:mob?54:68,background:"#fff",borderBottom:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",padding:mob?"0 14px":"0 20px",gap:mob?8:12,flexShrink:0,boxShadow:"0 2px 8px rgba(42,36,32,.07)",zIndex:100}}>
        <div style={{fontFamily:"'Lora',serif",fontSize:mob?18:24,fontWeight:600,color:"#2c2420",flexShrink:0,letterSpacing:"-.4px"}}>Study<span style={{color:a.main}}>Desk</span></div>
        {!mob&&<div style={{width:"1.5px",height:32,background:"#e0d9cf",flexShrink:0}}/>}
        {/* Desktop/tablet tab row */}
        {!mob&&(
          <div style={{display:"flex",gap:4,flex:1,overflowX:"auto",padding:"2px 0"}}>
            {TABS.map(t=><button key={t.k} onClick={()=>setTab(t.k)} style={{fontSize:isTablet?11:13,fontWeight:700,padding:isTablet?"6px 10px":"7px 14px",borderRadius:99,border:`1.5px solid ${tab===t.k?a.main:"transparent"}`,background:tab===t.k?a.light:"transparent",color:tab===t.k?a.main:"#9a9088",cursor:"pointer",transition:"all .14s",whiteSpace:"nowrap",flexShrink:0}}>{t.i} {t.l}</button>)}
          </div>
        )}
        <div style={{display:"flex",alignItems:"center",gap:mob?8:12,flexShrink:0,marginLeft:mob?"auto":0}}>
          {!mob&&(
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"'Lora',serif",fontSize:18,fontWeight:600,color:"#2c2420",lineHeight:1}}>{hh}:{mm}<span style={{fontSize:12,color:"#9a9088"}}>:{ss2}</span> <span style={{fontSize:11,color:"#9a9088",fontWeight:700}}>{ap}</span></div>
              <div style={{fontSize:11,color:"#9a9088",marginTop:2}}>{DAYS[now.getDay()]}, {MON[now.getMonth()]} {now.getDate()}</div>
            </div>
          )}
          <button onClick={()=>setShowSettings(true)} style={{width:mob?36:40,height:mob?36:40,borderRadius:"50%",background:a.light,border:`2px solid ${a.mid}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,color:a.main,fontSize:15,fontFamily:"'Nunito',sans-serif",overflow:"hidden",padding:0,flexShrink:0}} title={profile.name}>
            {profile.photo?<img src={profile.photo} alt={profile.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:profile.name[0].toUpperCase()}
          </button>
        </div>
      </div>
      {/* Content */}
      <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
        {tab==="planner"   &&<Planner     profile={profile} accent={profile.accent}/>}
        {tab==="classroom" &&<Classroom   profile={profile} accent={profile.accent}/>}
        {tab==="groups"    &&<StudyGroups profile={profile} accent={profile.accent}/>}
        {tab==="tutor"     &&<AiTutor     profile={profile} accent={profile.accent}/>}
        {tab==="friends"   &&<Friends     profile={profile} accent={profile.accent}/>}
      </div>
      {/* Mobile bottom nav */}
      {mob&&(
        <div style={{height:60,background:"#fff",borderTop:"1.5px solid #e0d9cf",display:"flex",alignItems:"center",justifyContent:"space-around",flexShrink:0,zIndex:100,paddingBottom:"env(safe-area-inset-bottom)"}}>
          {TABS.map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"6px 0",color:tab===t.k?a.main:"#9a9088"}}>
              <span style={{fontSize:20,lineHeight:1}}>{t.i}</span>
              <span style={{fontSize:9,fontWeight:800,letterSpacing:".3px"}}>{t.l}</span>
              {tab===t.k&&<div style={{width:16,height:2,background:a.main,borderRadius:99,marginTop:1}}/>}
            </button>
          ))}
        </div>
      )}
      {showSettings&&<Settings profile={profile} onUpdate={updateProf} onSwitchAccount={switchAccount} onNewAccount={newAccount} onLogout={signOut} onDeleteAccount={deleteAccount} onClose={()=>setShowSettings(false)}/>}
    </div>
  );
}
